import { useEffect, useMemo, useState } from "react";
import { api } from "@/api/client";
import { Avatar } from "@/components/Avatar";
import { type CardView } from "@/components/CardView";
import { PlatformLogo } from "@/components/PlatformLogo";

// Match the backend ``MarketListingOut`` schema shape. Promoted to module
// export so the Hot Offers page can render the same rich card.
export interface MarketListing {
  id: string;
  platform: "myhome" | "ss";
  external_id: string;
  published_url: string;
  status: string;

  title: string | null;
  full_description: string | null;
  short_description: string | null;

  price: string | null;
  currency: string | null;
  price_usd: string | null;

  rooms: number | null;
  bedrooms: number | null;
  area_sqm: string | null;
  property_type: string | null;
  floor: number | null;
  total_floors: number | null;

  district: string | null;
  neighborhood: string | null;
  street: string | null;

  deal_type: string | null;
  condition: string | null;
  building_status: string | null;

  cover_photo_url: string | null;
  photos: string[] | null;
  photo_count: number | null;
  features: string[] | null;
  amenities: string[] | null;

  welcomes_agents: boolean;
  forbids_agents: boolean;
  is_exclusive: boolean;
  owner_type: string | null;
  undeclared_agency?: "company" | "volume" | null;
  owner_phone: string | null;
  // Owner mobile a teammate saved by hand for this listing (per-agency; MyHome
  // masks the real number). Null when nobody at the agency has added one.
  manual_owner_phone: string | null;
  vip_level: string | null;

  // Price positioning vs the listing's (neighborhood, deal, property-type,
  // condition) cohort. Set together; null when no benchmark is available.
  price_per_sqm_usd: string | null;
  price_position: "cheap" | "average" | "expensive" | null;
  neighborhood_median_sqm_usd: number | null;
  price_percent_vs_median: number | null;
  neighborhood_sample_size: number | null;
  // Cohort tier used: l1_full (precise), l2_neighborhood_any (no condition
  // match — rough), l3_citywide (last-resort city comparison).
  price_benchmark_tier: string | null;

  listed_at: string | null;
  first_seen_at: string;
  last_seen_at: string;

  relevance: number | null;

  // Set when the viewer's agency already published this exact listing
  // (matched by platform + external_id). Drives the "uploaded by us"
  // avatar overlay on the card. Null when nobody at the agency placed it.
  placement_id: string | null;
  placed_by_user: {
    id: string;
    full_name: string | null;
    email: string;
    avatar_url: string | null;
  } | null;

  // Posting market-agency identity. Populated by the owner-finder (rolled up
  // to the parent firm) so the multi-agency scan can tag each match with the
  // competitor it came from. Null elsewhere.
  market_agency_id?: string | null;
  market_agency_name?: string | null;
  market_agency_logo_url?: string | null;
  market_agency_color_hex?: string | null;
}

export interface AIPick {
  listing: MarketListing;
  score: number;
  reasoning: string;
}

function formatUsd(v: string | null): string {
  if (!v) return "—";
  const n = Number(v);
  if (!Number.isFinite(n)) return v;
  return "$" + n.toLocaleString(undefined, { maximumFractionDigits: 0 });
}

function formatPriceNative(price: string | null, currency: string | null): string | null {
  if (!price) return null;
  const n = Number(price);
  if (!Number.isFinite(n)) return price;
  const sym =
    currency === "USD" ? "$" : currency === "GEL" ? "₾" : currency === "EUR" ? "€" : currency ? currency + " " : "";
  return `${sym}${n.toLocaleString(undefined, { maximumFractionDigits: 0 })}`;
}

function timeAgo(iso: string | null): string {
  if (!iso) return "—";
  const diff = (Date.now() - new Date(iso).getTime()) / 1000;
  if (diff < 60) return "just now";
  if (diff < 3600) return `${Math.round(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.round(diff / 3600)}h ago`;
  return `${Math.round(diff / 86400)}d ago`;
}

const DEAL_LABEL: Record<string, string> = {
  sale: "Sale",
  rent: "Rent",
  daily_rent: "Daily",
  mortgage: "Mortgage",
};
const PROPERTY_LABEL: Record<string, string> = {
  apartment: "Apartment",
  house: "House",
  cottage: "Cottage",
  land: "Land",
  commercial: "Commercial",
};

/** Small chip right under the price showing how this listing's $/m² compares
 *  to the median for its (neighborhood, deal, property-type) cohort.
 *  Colour + arrow direction are derived from ``price_position``; the
 *  percentage gives the magnitude. Hover reveals the full breakdown
 *  (neighborhood median, this listing's $/m², cohort sample size).
 *
 *  Silently renders nothing when we don't have enough data — either the
 *  listing's price_per_sqm is missing (no area, or no USD-normalized price),
 *  or the cohort has fewer than MIN_SAMPLE rows. */
function PricePositionChip({ listing: o }: { listing: MarketListing }) {
  if (!o.price_position || !o.price_per_sqm_usd) return null;
  const pct = o.price_percent_vs_median;
  const median = o.neighborhood_median_sqm_usd;
  const pps = Number(o.price_per_sqm_usd);
  if (median == null || pct == null) return null;

  // Colour + icon by category. "Average" is intentionally neutral so the
  // user's attention isn't pulled to listings that aren't outliers.
  const styles = {
    cheap: {
      classes: "bg-emerald-500/15 text-emerald-300 border-emerald-500/25",
      icon: "↓",
      verb: "below",
    },
    average: {
      classes: "bg-slate-500/10 text-slate-400 border-slate-500/20",
      icon: "≈",
      verb: "vs",
    },
    expensive: {
      classes: "bg-rose-500/15 text-rose-300 border-rose-500/25",
      icon: "↑",
      verb: "above",
    },
  }[o.price_position];

  // For "average", the pct is near 0 — don't waste space on "0.4% above";
  // just say "at median" instead.
  const shortLabel =
    o.price_position === "average"
      ? "At neighborhood median"
      : `${Math.abs(pct).toFixed(0)}% ${styles.verb} median`;

  const sample = o.neighborhood_sample_size ?? 0;
  // L1 = full precision (same neighborhood + condition), L2 = condition
  // ignored, L3 = citywide. Less-specific tiers get a dimmer chip so users
  // can weight the signal appropriately.
  const tier = o.price_benchmark_tier || "l1_full";
  const tierLabel =
    tier === "l1_full"
      ? "precise"
      : tier === "l2_neighborhood_any"
      ? "neighborhood avg"
      : "citywide avg";
  const tierCohort =
    tier === "l1_full"
      ? "same neighborhood + condition"
      : tier === "l2_neighborhood_any"
      ? "same neighborhood, any condition"
      : "citywide, any condition";
  const dim = tier !== "l1_full";

  const tooltip =
    `$${pps.toFixed(0)}/m² — ${shortLabel}\n` +
    `Reference median: $${median.toFixed(0)}/m² (${sample} listings, ${tierCohort})`;

  return (
    <div
      className={`inline-flex items-center gap-1 mt-1 px-1.5 py-0.5 rounded text-[10px] font-medium border ${styles.classes} ${
        dim ? "opacity-70" : ""
      }`}
      title={tooltip}
    >
      <span className="leading-none">{styles.icon}</span>
      <span>{shortLabel}</span>
      {dim && (
        <span className="text-[9px] opacity-70 italic ml-0.5">
          ({tierLabel})
        </span>
      )}
    </div>
  );
}

/** Small avatar chip surfaced on offer cards when the viewer's agency has
 *  already placed this exact listing (matched by platform + external_id).
 *  Hover reveals the placer's name. Emerald ring + tooltip make the
 *  "uploaded by us" status readable at a glance so brokers don't double-
 *  publish what a teammate already handled. */
function PlacedByUsChip({
  user,
}: {
  user: NonNullable<MarketListing["placed_by_user"]>;
}) {
  const displayName = user.full_name || user.email;
  return (
    <div className="group/placed relative flex items-center">
      <Avatar
        url={user.avatar_url}
        name={user.full_name}
        email={user.email}
        size="xs"
        className="ring-2 ring-emerald-400/90 shadow-md"
        alt={`Uploaded by ${displayName}`}
      />
      <span
        className="pointer-events-none absolute right-full top-1/2 -translate-y-1/2 mr-1.5 whitespace-nowrap rounded bg-black/85 px-1.5 py-0.5 text-[10px] font-medium text-emerald-100 ring-1 ring-emerald-400/40 shadow-md opacity-0 transition group-hover/placed:opacity-100"
        role="tooltip"
      >
        Uploaded by {displayName}
      </span>
    </div>
  );
}

// Listing description — collapsed to a few lines by default with a Show more /
// Show less toggle, mirroring the Placements card. Renders the short blurb
// when collapsed and the fuller body (when there is one) once expanded.
function DescriptionBlock({
  short,
  full,
}: {
  short: string | null;
  full: string | null;
}) {
  const [expanded, setExpanded] = useState(false);
  const collapsedText = short ?? full;
  const fullText = full ?? short;
  if (!collapsedText) return null;
  // Worth a toggle when the full body carries more than the short blurb, or the
  // collapsed text is long enough that the 3-line clamp actually hides part of
  // it. The 140-char threshold is a rough proxy for "longer than ~3 lines".
  const clampable =
    (!!fullText && fullText.length > collapsedText.length) ||
    collapsedText.length > 140;
  return (
    <div className="text-[12px] text-slate-400 leading-relaxed">
      <div
        className={
          expanded || !clampable
            ? "whitespace-pre-line"
            : "whitespace-pre-line line-clamp-3"
        }
      >
        {expanded ? fullText : collapsedText}
      </div>
      {clampable && (
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            setExpanded((v) => !v);
          }}
          className="mt-0.5 text-[11px] font-medium text-accent-400 hover:text-accent-300"
        >
          {expanded ? "Show less" : "Show more"}
        </button>
      )}
    </div>
  );
}

/** Market card — mirrors the Hot Offers card layout (aspect-4/3 photo with
 *  corner badges, price + timeago row, icon-labelled specs, district,
 *  description). Differences from Hot Offers:
 *   - no "claim" button (Market is read-only; ownership is an agency concern)
 *   - shows USD-normalized price on top, native price below when different
 *   - surfaces additional provenance badges: agency, exclusive, no-agents
 *   - when Gemini rankedthis card, the reasoning slides in below the
 *     description in an accent-tinted block */
export function MarketCard({
  listing: o,
  pick,
  selectable = false,
  selected = false,
  onToggleSelect,
  actions,
  variant = "comfortable",
  showOwnerPhone = false,
  onOwnerPhoneSaved,
}: {
  listing: MarketListing;
  pick?: AIPick;
  /** Selection-mode props for the share-link flow. When ``selectable`` is
   *  true, clicking the cover photo toggles selection instead of opening
   *  the source URL, and a checkbox overlay appears on the card. */
  selectable?: boolean;
  selected?: boolean;
  onToggleSelect?: () => void;
  /** Optional footer slot — Hot Offers uses this to render a Claim button
   *  alongside the "Open on portal" link. Rendered inside the card body
   *  below the description. */
  actions?: React.ReactNode;
  /** Employee-chosen layout: comfortable (default) | compact | list. */
  variant?: CardView;
  /** Show the owner's mobile number on the card (Owner Listings uses this for
   *  SS listings, which carry an ``owner_phone``). When a card has no scraped
   *  phone (MyHome masks them) an inline "add owner phone" editor is shown
   *  instead, so agents can record a number they sourced by hand. */
  showOwnerPhone?: boolean;
  /** Called after a manual owner phone is saved/cleared on this card, with the
   *  new value (null when cleared). Lets the parent keep its listing in sync so
   *  the saved number flows into the publish modal's owner-phone prefill. */
  onOwnerPhoneSaved?: (phone: string | null) => void;
}) {
  const priceUsd = o.price_usd ? formatUsd(o.price_usd) : null;
  const priceNative = formatPriceNative(o.price, o.currency);
  const showNativeSecondary =
    priceNative !== null && (!priceUsd || o.currency !== "USD");

  const dealLabel = o.deal_type ? DEAL_LABEL[o.deal_type] ?? o.deal_type : null;
  const typeLabel = o.property_type ? PROPERTY_LABEL[o.property_type] ?? o.property_type : null;
  const areaText = o.area_sqm
    ? `${Number(o.area_sqm).toLocaleString(undefined, { maximumFractionDigits: 0 })} m²`
    : null;
  const floorText = o.floor
    ? o.total_floors
      ? `${o.floor}/${o.total_floors}`
      : `${o.floor}`
    : null;

  const highlightAgent = o.welcomes_agents;
  const highlightPick = !!pick;

  const selectionBorder = selectable
    ? selected
      ? "border-accent-400 shadow-[0_0_0_2px_rgba(99,102,241,0.45)]"
      : ""
    : "";
  const handleCoverClick = (e: React.MouseEvent<HTMLAnchorElement>) => {
    if (selectable) {
      e.preventDefault();
      onToggleSelect?.();
    }
  };

  // Carousel: cover + extras, deduped. The cover URL almost always
  // appears as the first entry of `photos` too, so de-dup so the user
  // doesn't see the same shot twice when paging.
  const allPhotos = useMemo(() => {
    const seen = new Set<string>();
    const out: string[] = [];
    if (o.cover_photo_url) {
      out.push(o.cover_photo_url);
      seen.add(o.cover_photo_url);
    }
    for (const p of o.photos ?? []) {
      if (p && !seen.has(p)) {
        out.push(p);
        seen.add(p);
      }
    }
    return out;
  }, [o.cover_photo_url, o.photos]);
  const [photoIdx, setPhotoIdx] = useState(0);
  const photoSrc = allPhotos[photoIdx] ?? null;
  const hasMany = allPhotos.length > 1;
  const stepPhoto = (dir: -1 | 1) => (e: React.MouseEvent) => {
    // Block the surrounding anchor: clicking an arrow must not navigate
    // to the portal or toggle the selectable checkbox.
    e.preventDefault();
    e.stopPropagation();
    setPhotoIdx((i) =>
      Math.min(allPhotos.length - 1, Math.max(0, i + dir)),
    );
  };

  const isCompact = variant === "compact";
  const isList = variant === "list";

  // Owner mobile. Portals mask numbers: MyHome hands back a truncated
  // ``551212***`` for every listing, which is useless to dial and must never
  // be shown. Treat any value containing ``*`` as "no real number". A genuine
  // phone (SS exposes them in full) renders as a read-only tap-to-call chip;
  // otherwise we show an inline editor so an agent can record the real number
  // they sourced by hand — saved per-agency and surfaced as ``manual_owner_phone``.
  const scrapedPhone =
    o.owner_phone && !o.owner_phone.includes("*") ? o.owner_phone : null;
  let phoneChip: React.ReactNode = null;
  if (showOwnerPhone) {
    if (scrapedPhone) {
      phoneChip = (
        <a
          href={`tel:${scrapedPhone}`}
          onClick={(e) => e.stopPropagation()}
          className="inline-flex w-fit items-center gap-1.5 text-[12px] font-semibold text-emerald-300 hover:text-emerald-200 transition-colors"
          title="Owner mobile (from the listing)"
        >
          <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72c.13.96.36 1.9.7 2.81a2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45c.91.34 1.85.57 2.81.7A2 2 0 0 1 22 16.92z" />
          </svg>
          {scrapedPhone}
        </a>
      );
    } else {
      phoneChip = (
        <OwnerPhoneField
          platform={o.platform}
          externalId={o.external_id}
          initialPhone={o.manual_owner_phone}
          onSaved={onOwnerPhoneSaved}
        />
      );
    }
  }

  return (
    <div
      className={`card overflow-hidden flex ${isList ? "flex-row" : "flex-col"} group/card transition ${
        highlightPick
          ? "border-accent-500/50 shadow-[0_0_0_1px_rgba(124,92,255,0.25),0_0_32px_-8px_rgba(124,92,255,0.45)] hover:border-accent-400/70"
          : highlightAgent
          ? "border-amber-400/40 shadow-[0_0_0_1px_rgba(251,191,36,0.15),0_0_32px_-8px_rgba(251,191,36,0.35)] hover:border-amber-400/60"
          : "hover:border-accent-500/30"
      } ${selectionBorder}`}
    >
      <a
        href={o.published_url}
        target="_blank"
        rel="noopener noreferrer"
        onClick={handleCoverClick}
        className={`relative bg-white/[0.03] overflow-hidden ${
          isList
            ? "block w-28 h-24 sm:w-44 sm:h-32 shrink-0 self-start"
            : isCompact
            ? "block aspect-[16/10]"
            : "block aspect-[4/3]"
        } ${selectable ? "cursor-pointer" : ""}`}
      >
        {photoSrc ? (
          <img
            // ``key`` forces a fresh DOM node per src, so an inline
            // ``display: none`` set by a prior onError doesn't carry
            // over and hide a working photo the user paged to.
            key={photoSrc}
            src={photoSrc}
            alt=""
            loading="lazy"
            className="w-full h-full object-cover group-hover/card:scale-105 transition-transform duration-300"
            onError={(e) => {
              (e.currentTarget as HTMLImageElement).style.display = "none";
            }}
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center text-slate-700 text-xs">
            no photo
          </div>
        )}

        {/* Carousel arrows. Hidden on desktop until the card is hovered
            (or the button gets keyboard focus); always visible on
            touch where there is no hover state. Arrow clicks must stop
            propagation so they don't trigger the cover anchor. */}
        {hasMany && !isList && (
          <>
            <button
              type="button"
              onClick={stepPhoto(-1)}
              disabled={photoIdx === 0}
              aria-label="Previous photo"
              className="absolute left-1 top-1/2 -translate-y-1/2 h-8 w-8 rounded-full bg-black/70 text-white shadow-md backdrop-blur-sm flex items-center justify-center transition hover:bg-black/90 disabled:opacity-30 disabled:cursor-not-allowed disabled:hover:bg-black/70"
            >
              <svg
                width="16"
                height="16"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <polyline points="15 18 9 12 15 6" />
              </svg>
            </button>
            <button
              type="button"
              onClick={stepPhoto(1)}
              disabled={photoIdx >= allPhotos.length - 1}
              aria-label="Next photo"
              className="absolute right-1 top-1/2 -translate-y-1/2 h-8 w-8 rounded-full bg-black/70 text-white shadow-md backdrop-blur-sm flex items-center justify-center transition hover:bg-black/90 disabled:opacity-30 disabled:cursor-not-allowed disabled:hover:bg-black/70"
            >
              <svg
                width="16"
                height="16"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <polyline points="9 18 15 12 9 6" />
              </svg>
            </button>
          </>
        )}

        {/* Top-left: platform logo + welcomes-agents highlight + Gemini rank.
            In selection mode the checkbox leads so the broker spots
            picked cards at a glance. */}
        <div className="absolute top-2 left-2 flex items-center gap-1.5">
          {selectable && (
            <span
              className={`flex items-center justify-center w-6 h-6 rounded-md border-2 transition ${
                selected
                  ? "bg-accent-500 border-accent-300 text-white"
                  : "bg-ink-950/70 border-white/40 text-transparent backdrop-blur-sm"
              }`}
              aria-label={selected ? "Deselect" : "Select"}
            >
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                <polyline points="20 6 9 17 4 12" />
              </svg>
            </span>
          )}
          <PlatformLogo platform={o.platform} size="sm" />
          {o.welcomes_agents && (
            <span
              className="badge text-[10px] border-amber-400/60 bg-black/65 text-amber-200 shadow-md backdrop-blur-sm"
              title="Owner explicitly welcomes agencies — priority lead"
            >
              🔥 Hot
            </span>
          )}
          {pick && (
            <span
              className="badge text-[10px] border-accent-500/60 bg-black/65 text-accent-200 shadow-md backdrop-blur-sm"
              title={`Gemini ranked this ${(pick.score * 100).toFixed(0)}%`}
            >
              ✨ {(pick.score * 100).toFixed(0)}%
            </span>
          )}
        </div>

        {/* Top-right: deal type + "uploaded by us" indicator. Stacked so the
            avatar sits beneath the deal-type chip without colliding with
            either the bottom-right photo counter or the cover-link tap area. */}
        {(dealLabel || o.placed_by_user) && (
          <div className="absolute top-2 right-2 flex flex-col items-end gap-1.5">
            {dealLabel && (
              <span
                className={`badge text-[10px] bg-black/65 shadow-md backdrop-blur-sm ${
                  o.deal_type === "sale"
                    ? "border-accent-500/60 text-accent-200"
                    : "border-emerald-500/60 text-emerald-200"
                }`}
              >
                {dealLabel}
              </span>
            )}
            {o.placed_by_user && <PlacedByUsChip user={o.placed_by_user} />}
          </div>
        )}

        {/* Bottom-left: provenance badges (hidden on the tiny list thumb) */}
        {!isList && (
        <div className="absolute bottom-2 left-2 flex items-center gap-1.5 flex-wrap max-w-[70%]">
          {o.owner_type === "agency" && (
            <span
              className={`badge text-[10px] bg-black/70 shadow-md backdrop-blur-sm ${
                o.undeclared_agency
                  ? "border-amber-400/60 text-amber-200"
                  : "border-white/30 text-slate-100"
              }`}
              title={
                o.undeclared_agency === "company"
                  ? "Registered as a private seller, but the portal links this account to a registered agency"
                  : o.undeclared_agency === "volume"
                    ? "Registered as a private seller, but this account posts at agency volume"
                    : "Posted by an agency"
              }
            >
              {o.undeclared_agency ? "agency (undeclared)" : "agency"}
            </span>
          )}
          {o.is_exclusive && (
            <span
              className="badge text-[10px] border-rose-400/60 bg-black/70 text-rose-200 shadow-md backdrop-blur-sm"
              title="Exclusive to another agency — can't republish"
            >
              exclusive
            </span>
          )}
          {o.forbids_agents && (
            <span
              className="badge text-[10px] border-amber-400/60 bg-black/70 text-amber-200 shadow-md backdrop-blur-sm"
              title="Owner explicitly asked for no agencies"
            >
              ⚠ no agents
            </span>
          )}
        </div>
        )}

        {/* Bottom-right: photo count + vip badge (hidden on the list thumb) */}
        {!isList && (
        <div className="absolute bottom-2 right-2 flex items-center gap-1.5">
          {o.vip_level && (
            <span
              className="badge text-[10px] border-yellow-400/60 bg-black/70 text-yellow-200 shadow-md backdrop-blur-sm"
              title="Portal-promoted listing"
            >
              {o.vip_level.replace("_", " ")}
            </span>
          )}
          {o.photo_count ? (
            <span className="badge text-[10px] border-white/20 bg-black/70 text-slate-100 shadow-md backdrop-blur-sm tabular-nums">
              📷 {hasMany ? `${photoIdx + 1}/${allPhotos.length}` : o.photo_count}
            </span>
          ) : null}
        </div>
        )}
      </a>

      <div
        className={`flex flex-col flex-1 min-w-0 ${
          isCompact || isList ? "p-2.5 gap-1.5" : "p-3 gap-2"
        }`}
      >
        <div className="flex items-start justify-between gap-2">
          <div>
            <div className="text-base font-semibold text-slate-100 tabular-nums">
              {priceUsd || priceNative || <span className="text-slate-500">—</span>}
            </div>
            {showNativeSecondary && priceNative && priceUsd && (
              <div className="text-[11px] text-slate-500 tabular-nums">{priceNative}</div>
            )}
            <PricePositionChip listing={o} />
          </div>
          <div
            className="text-[11px] text-slate-500 whitespace-nowrap"
            title={o.listed_at || o.first_seen_at}
          >
            {timeAgo(o.listed_at || o.first_seen_at)}
          </div>
        </div>

        {/* Listing title from the source portal (e.g. "ქირავდება 2 ოთახიანი
            ბინა ვაკეში"). Two-line clamp keeps card heights consistent; full
            text is available via the native title tooltip on hover. */}
        {o.title && (
          <h3
            className={`text-[13px] leading-snug font-medium text-slate-200 ${
              isCompact || isList ? "line-clamp-1" : "line-clamp-2"
            }`}
            title={o.title}
          >
            {o.title}
          </h3>
        )}

        {(typeLabel || o.rooms || o.bedrooms || areaText || floorText) && (
          <div className="text-[12px] text-slate-400 flex flex-wrap items-center gap-x-3 gap-y-1">
            {typeLabel && <span>{typeLabel}</span>}
            {o.rooms && (
              <span className="inline-flex items-center gap-1" title={`${o.rooms} rooms total`}>
                <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M6 22V4a2 2 0 0 1 2-2h8a2 2 0 0 1 2 2v18" />
                  <path d="M3 22h18" />
                  <circle cx="14" cy="13" r="0.6" fill="currentColor" />
                </svg>
                {o.rooms}
              </span>
            )}
            {o.bedrooms != null && o.bedrooms > 0 && (
              <span
                className="inline-flex items-center gap-1"
                title={`${o.bedrooms} bedroom${o.bedrooms > 1 ? "s" : ""}`}
              >
                {/* bed icon — distinguishes bedroom count from total rooms */}
                <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M2 4v16" />
                  <path d="M2 8h18a2 2 0 0 1 2 2v10" />
                  <path d="M2 17h20" />
                  <path d="M6 8v9" />
                </svg>
                {o.bedrooms}
              </span>
            )}
            {areaText && (
              <span className="inline-flex items-center gap-1" title="Area">
                <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <rect x="3" y="3" width="18" height="18" rx="2" />
                  <path d="M3 9h4M3 15h4M9 3v4M15 3v4" />
                </svg>
                {areaText}
              </span>
            )}
            {floorText && (
              <span
                className="inline-flex items-center gap-1"
                title={o.total_floors ? `Floor ${o.floor} of ${o.total_floors}` : `Floor ${o.floor}`}
              >
                <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M12 2 2 7l10 5 10-5-10-5z" />
                  <path d="M2 12l10 5 10-5" />
                  <path d="M2 17l10 5 10-5" />
                </svg>
                {floorText}
              </span>
            )}
          </div>
        )}

        {(o.neighborhood || o.district) && (
          <div className="text-[12px] text-slate-500 flex items-center gap-1">
            <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0118 0z" />
              <circle cx="12" cy="10" r="3" />
            </svg>
            {/* Neighborhood is our canonical fine-grained label; district is
                the portal's coarse umbrella (e.g. "ვაკე-საბურთალო"). Show
                neighborhood when we have it, district as fallback. */}
            {o.neighborhood ? (
              <span>
                <span className="text-slate-300">
                  {NEIGHBORHOOD_LABELS[o.neighborhood] ?? o.neighborhood}
                </span>
                {o.district && (
                  <span className="text-slate-600"> · {o.district}</span>
                )}
              </span>
            ) : (
              o.district
            )}
          </div>
        )}

        {pick?.reasoning ? (
          <div className="text-[12px] leading-relaxed text-accent-200/90 bg-accent-500/5 border border-accent-500/15 rounded-md px-2.5 py-2">
            <span className="inline-block text-[10px] font-bold uppercase tracking-wider text-accent-400 mr-1.5">
              Gemini
            </span>
            {pick.reasoning}
          </div>
        ) : (
          !isCompact && !isList && (
            <DescriptionBlock short={o.short_description} full={o.full_description} />
          )
        )}

        {phoneChip}
        {actions && <div className="mt-auto pt-1">{actions}</div>}
      </div>
    </div>
  );
}

/** Inline editor for a manually-sourced owner mobile on a market card. MyHome
 *  masks phones, so agents track them down and record them here — persisted
 *  per-agency via ``PUT /api/owner-listings/owner-phone`` and echoed back on
 *  the card. Three states: a tap-to-call chip + "edit" when a number exists,
 *  an input row while editing, and a subtle "add" button when empty. All
 *  clicks stop propagation so they never trigger the card's cover anchor. */
function OwnerPhoneField({
  platform,
  externalId,
  initialPhone,
  onSaved,
}: {
  platform: string;
  externalId: string;
  initialPhone: string | null;
  onSaved?: (phone: string | null) => void;
}) {
  const [phone, setPhone] = useState<string | null>(initialPhone);
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(initialPhone ?? "");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(false);

  // Re-sync when the parent swaps in a fresh listing object (the 60s
  // auto-refresh rebuilds rows). Don't clobber an in-progress edit.
  useEffect(() => {
    setPhone(initialPhone);
    if (!editing) setDraft(initialPhone ?? "");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialPhone]);

  const stop = (e: React.MouseEvent) => e.stopPropagation();

  async function save() {
    const next = draft.trim();
    setSaving(true);
    setError(false);
    try {
      const r = await api<{ manual_owner_phone: string | null }>(
        "/api/owner-listings/owner-phone",
        {
          method: "PUT",
          json: { platform, external_id: externalId, phone: next },
        },
      );
      setPhone(r.manual_owner_phone);
      setDraft(r.manual_owner_phone ?? "");
      setEditing(false);
      onSaved?.(r.manual_owner_phone);
    } catch {
      setError(true);
    } finally {
      setSaving(false);
    }
  }

  function cancel() {
    setEditing(false);
    setDraft(phone ?? "");
    setError(false);
  }

  if (editing) {
    return (
      <div className="flex items-center gap-1.5 flex-wrap" onClick={stop}>
        <input
          type="tel"
          inputMode="tel"
          autoFocus
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              e.preventDefault();
              save();
            } else if (e.key === "Escape") {
              e.preventDefault();
              cancel();
            }
          }}
          placeholder="Owner mobile"
          disabled={saving}
          className="input py-1 px-2 text-[12px] w-32 mono"
        />
        <button
          type="button"
          onClick={save}
          disabled={saving}
          className="text-[11px] font-semibold px-2 py-1 rounded-md bg-emerald-500/20 text-emerald-200 border border-emerald-500/30 hover:bg-emerald-500/30 disabled:opacity-50"
        >
          {saving ? "…" : "Save"}
        </button>
        <button
          type="button"
          onClick={cancel}
          disabled={saving}
          className="text-[11px] text-slate-500 hover:text-slate-300 px-1"
          aria-label="Cancel"
        >
          ✕
        </button>
        {error && <span className="text-[10px] text-rose-300/80">save failed</span>}
      </div>
    );
  }

  if (phone) {
    return (
      <div className="flex items-center gap-2" onClick={stop}>
        <a
          href={`tel:${phone}`}
          className="inline-flex w-fit items-center gap-1.5 text-[12px] font-semibold text-emerald-300 hover:text-emerald-200 transition-colors"
          title="Owner mobile (added by your team)"
        >
          <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72c.13.96.36 1.9.7 2.81a2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45c.91.34 1.85.57 2.81.7A2 2 0 0 1 22 16.92z" />
          </svg>
          {phone}
        </a>
        <button
          type="button"
          onClick={() => {
            setDraft(phone);
            setEditing(true);
          }}
          className="text-[11px] text-slate-500 hover:text-accent-300"
          title="Edit owner mobile"
        >
          edit
        </button>
      </div>
    );
  }

  return (
    <button
      type="button"
      onClick={(e) => {
        stop(e);
        setDraft("");
        setEditing(true);
      }}
      className="inline-flex w-fit items-center gap-1.5 text-[12px] font-medium text-slate-500 hover:text-emerald-300 transition-colors"
      title="Add the owner's mobile number (visible to your agency only)"
    >
      <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72c.13.96.36 1.9.7 2.81a2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45c.91.34 1.85.57 2.81.7A2 2 0 0 1 22 16.92z" />
      </svg>
      + Add owner phone
    </button>
  );
}

export type DealType = "sale" | "rent";
export type PropertyType = "apartment" | "house" | "cottage" | "land" | "commercial";
export type SortKey =
  | "newest"
  | "oldest"
  | "price_asc"
  | "price_desc"
  | "area_desc"
  | "best_deal"
  | "price_per_sqm_asc"
  | "price_per_sqm_desc";

// The shape of filters the UI manages. Maps 1:1 to MarketSearchIn where names
// match; ``rooms_pick`` is a UI-only multi-select that the request builder
// collapses into ``rooms_min`` / ``rooms_max`` at send time.
export interface Filters {
  deal_type?: DealType;
  property_type?: PropertyType;
  // Source portal restriction. Useful when an agent only wants leads from
  // the portal where they have an active relationship.
  platform?: PlatformType;
  rooms_pick?: number[]; // elements in [1..5]; 5 means "5+"
  // Bedrooms is conceptually a subset of rooms — a 3-room apartment is often
  // "1 bedroom + 1 living + 1 kitchen-diner". Kept separate because Georgian
  // real-estate listings advertise them separately and buyers search on it.
  bedrooms_pick?: number[]; // elements in [1..5]; 5 means "5+"
  bathrooms_pick?: number[]; // elements in [1..4]; 4 means "4+"
  price_min_usd?: number;
  price_max_usd?: number;
  /** Which currency the price bounds above are typed in. Georgian listings
   *  are quoted in lari, so a broker thinking "up to 3,000 ₾" shouldn't
   *  have to convert in their head — with GEL the server compares against
   *  the portal's own lari figure, not a converted one. */
  price_currency?: "USD" | "GEL";
  // m² range. Both portals publish area so coverage is close to 100% for
  // apartments; land listings are the main area-missing cohort and filtering
  // by them is rare anyway.
  area_min_sqm?: number;
  area_max_sqm?: number;
  // Apartment floor in the building (and number of stories total).
  floor_min?: number;
  floor_max?: number;
  total_floors_min?: number;
  total_floors_max?: number;
  // Year-of-construction band. Coverage is partial — many old-build rows
  // omit this — so it's more useful as a "newer than YYYY" cutoff than a
  // strict range.
  year_built_min?: number;
  year_built_max?: number;
  // Canonical neighborhood codes (see NEIGHBORHOOD_GROUPS). Finer-grained
  // than the portals' district labels — "ვაკე-საბურთალო" in the portal
  // data actually contains ~9 distinct neighborhoods.
  neighborhoods?: string[];
  // Normalized street name tokens picked from the catalog. Backend ILIKEs
  // these against ``HotOffer.street`` (substring, OR semantics across the
  // list). Combined with ``neighborhoods`` the two AND together — e.g.
  // "in Vake OR Saburtalo, AND on a street matching X or Y".
  streets?: string[];
  // Multi-select: include a listing if its condition / building_status is
  // ANY of the picked values (disjunction within each filter).
  condition?: string[]; // codes from CONDITION_OPTIONS
  building_status?: string[]; // codes from BUILDING_STATUS_OPTIONS
  // Architectural / project-type codes (Khrushchev, Italian yard, …) — the
  // portals' ``project_type_id`` flattened into a single canonical list.
  project_type?: string[]; // codes from PROJECT_TYPE_OPTIONS
  sort?: SortKey;
  // Agent-posture exclusions, each toggleable independently in the toolbar.
  // Legacy ``owner_direct`` (URL ``dir=1`` / localStorage) is migrated to all
  // three on parse so old bookmarks still behave the same.
  exclude_agency?: boolean;
  exclude_no_agents?: boolean;
  exclude_exclusive?: boolean;
  // Limits the result set against the viewer's agency placements (matched
  // by platform + external_id). ``placed`` = only listings we've already
  // published; ``not_placed`` = only listings nobody at the agency has
  // touched. Undefined means "no filter" (default).
  placed_by_us?: "placed" | "not_placed";
}

export type PlatformType = "myhome" | "ss";

// Fine-grained Tbilisi neighborhoods, grouped by macro-region. Grouping
// makes the popover scannable: users find the cluster they care about
// ("Old Tbilisi", "Vake-adjacent"), then pick a specific neighborhood.
//
// Sourced from ``frontend/src/lib/neighborhoodVocab.ts``, which is
// auto-generated from the ``myhome_districts`` / ``myhome_urbans``
// tables (see ``scripts/regen_neighborhood_vocab.py``). Backend tagger
// (``app/services/neighborhood_vocab.py``) is generated from the same
// source so the UI dropdown can never drift from the codes the
// pipeline actually writes onto ``HotOffer.neighborhood``.
export {
  NEIGHBORHOOD_GROUPS,
  NEIGHBORHOOD_LABELS,
  NEIGHBORHOOD_LABELS_KA,
  NEIGHBORHOOD_OLD_TO_NEW,
} from "@/lib/neighborhoodVocab";
import {
  NEIGHBORHOOD_GROUPS,
  NEIGHBORHOOD_LABELS,
  NEIGHBORHOOD_LABELS_KA,
} from "@/lib/neighborhoodVocab";

const PROPERTY_OPTIONS: { value: PropertyType; label: string }[] = [
  { value: "apartment", label: "Apartment" },
  { value: "house", label: "House" },
  { value: "cottage", label: "Cottage" },
  { value: "land", label: "Land" },
  { value: "commercial", label: "Commercial" },
];

// Canonical codes mirror ``HotOffer.building_status`` (see model + benchmarks).
// Georgian labels are primary because the portals + agents think in Georgian;
// the English tail is for mixed-language teams.
export const BUILDING_STATUS_OPTIONS: { value: string; label: string; en: string }[] = [
  { value: "new_built", label: "ახალი აშენებული", en: "New build" },
  { value: "under_construction", label: "მშენებარე", en: "Under construction" },
  { value: "old_built", label: "ძველი აშენებული", en: "Old build" },
];

// Canonical codes mirror ``HotOffer.condition``. Covers the full union of
// what myhome.ge and ss.ge publish — ordered from "ready to live" down to
// "bare walls" so agents usually scan the most common choice first.
/** Renovation condition. Every code here is produced by BOTH portals in
 *  ``market_listings`` (see migration mktlive04) except ``renovated``,
 *  which only ss.ge publishes — it means "renovated, age unspecified" and
 *  covers ~21k listings that no other chip can reach, so it is offered
 *  rather than hidden. MyHome's ``white_plus`` is deliberately absent: it
 *  is a listing tier, not a condition, and covers 844 rows. */
export const CONDITION_OPTIONS: { value: string; label: string; en: string }[] = [
  { value: "new_renovation", label: "ახალი გარემონტებული", en: "Newly renovated" },
  { value: "old_renovation", label: "ძველი რემონტით", en: "Old renovation" },
  { value: "renovated", label: "გარემონტებული", en: "Renovated" },
  { value: "ongoing_renovation", label: "მიმდინარე რემონტი", en: "Renovation in progress" },
  { value: "needs_renovation", label: "საჭიროებს რემონტს", en: "Needs renovation" },
  { value: "green_frame", label: "მწვანე კარკასი", en: "Green frame" },
  { value: "white_frame", label: "თეთრი კარკასი", en: "White frame" },
  { value: "black_frame", label: "შავი კარკასი", en: "Black frame" },
];

// Architectural / project-type codes mirror ``HotOffer.project_type``. The
// labels mix Georgian + an English gloss because some of these are folk
// terms ("Italian yard", "Khrushchev-era") with no standard English
// translation. Grouped: modern → Soviet-era panels → courtyard styles.
export const PROJECT_TYPE_OPTIONS: { value: string; label: string; en: string }[] = [
  { value: "non_standard", label: "არასტანდარტული", en: "Non-standard" },
  { value: "urban", label: "ურბანული", en: "Modern urban" },
  { value: "soviet_typical", label: "ტიპიური საბჭოთა", en: "Soviet typical" },
  { value: "khrushchev", label: "ხრუშჩოვკა", en: "Khrushchev-era" },
  { value: "moscow", label: "მოსკოვური", en: "Moscow" },
  { value: "leningrad", label: "ლენინგრადული", en: "Leningrad" },
  { value: "kiev", label: "კიევური", en: "Kiev" },
  { value: "czech", label: "ჩეხური პროექტი", en: "Czech" },
  { value: "lvov", label: "ლვოვის პროექტი", en: "Lvov" },
  { value: "yugoslavia", label: "იუგოსლავიური", en: "Yugoslav" },
  { value: "italian_yard", label: "იტალიური ეზო", en: "Italian yard" },
  { value: "tbilisi_yard", label: "თბილისური ეზო", en: "Tbilisi yard" },
  { value: "kavlashvili", label: "კავლაშვილის", en: "Kavlashvili" },
  { value: "tukhareli", label: "თუხარელის", en: "Tukhareli" },
  { value: "vedzisi", label: "ვეძისის", en: "Vedzisi" },
  { value: "metromsheni", label: "მეტრომშენი", en: "Metromsheni" },
  { value: "caucasian", label: "კავკასიური", en: "Caucasian" },
];

// Source-portal options. Two-only switch — keeping it as a list keeps room
// for a future portal addition without reshaping the filter's URL key.
const PLATFORM_OPTIONS: { value: PlatformType; label: string }[] = [
  { value: "myhome", label: "MyHome" },
  { value: "ss", label: "SS.ge" },
];

// Sort options exposed in the UI. Grouped conceptually in the dropdown:
//   Freshness:     newest, oldest
//   Absolute $:    price_asc, price_desc
//   Value / deal:  best_deal  (most below neighborhood median)
//   $/m²:          price_per_sqm_asc, price_per_sqm_desc
//   Size:          area_desc
// The MenuItem separators between groups are rendered in the SortDropdown.
const SORT_OPTIONS: { value: SortKey; label: string; group: string }[] = [
  { value: "newest", label: "Newest", group: "fresh" },
  { value: "oldest", label: "Oldest", group: "fresh" },
  { value: "best_deal", label: "✨ Best deal", group: "deal" },
  { value: "price_per_sqm_asc", label: "Cheapest per m²", group: "deal" },
  { value: "price_per_sqm_desc", label: "Most expensive per m²", group: "deal" },
  { value: "price_asc", label: "Cheapest total", group: "price" },
  { value: "price_desc", label: "Most expensive total", group: "price" },
  { value: "area_desc", label: "Biggest", group: "size" },
];

/** Collapse UI filters into the wire shape ``MarketSearchIn`` expects. */
export function filtersToBody(f: Filters): Record<string, unknown> {
  const body: Record<string, unknown> = {};
  if (f.deal_type) body.deal_type = f.deal_type;
  if (f.property_type) body.property_type = f.property_type;
  if (f.platform) body.platform = f.platform;
  if (f.rooms_pick?.length) {
    const picks = [...f.rooms_pick].sort((a, b) => a - b);
    body.rooms_min = picks[0];
    // "5+" picked → no upper bound; otherwise clamp to the highest pick.
    if (!picks.includes(5)) body.rooms_max = picks[picks.length - 1];
  }
  if (f.bedrooms_pick?.length) {
    const picks = [...f.bedrooms_pick].sort((a, b) => a - b);
    body.bedrooms_min = picks[0];
    if (!picks.includes(5)) body.bedrooms_max = picks[picks.length - 1];
  }
  if (f.bathrooms_pick?.length) {
    const picks = [...f.bathrooms_pick].sort((a, b) => a - b);
    body.bathrooms_min = picks[0];
    // "4+" picked → no upper bound (bathrooms cap at 4 in the picker).
    if (!picks.includes(4)) body.bathrooms_max = picks[picks.length - 1];
  }
  // Sent as the currency-tagged pair, which the server reads in
  // ``price_currency``. The legacy ``price_min_usd`` / ``price_max_usd``
  // fields stay USD-only and are what the CRM matcher still builds.
  if (f.price_min_usd != null) body.price_min = f.price_min_usd;
  if (f.price_max_usd != null) body.price_max = f.price_max_usd;
  if (f.price_min_usd != null || f.price_max_usd != null) {
    body.price_currency = f.price_currency ?? "USD";
  }
  if (f.area_min_sqm != null) body.area_min = f.area_min_sqm;
  if (f.area_max_sqm != null) body.area_max = f.area_max_sqm;
  if (f.floor_min != null) body.floor_min = f.floor_min;
  if (f.floor_max != null) body.floor_max = f.floor_max;
  if (f.total_floors_min != null) body.total_floors_min = f.total_floors_min;
  if (f.total_floors_max != null) body.total_floors_max = f.total_floors_max;
  if (f.year_built_min != null) body.year_built_min = f.year_built_min;
  if (f.year_built_max != null) body.year_built_max = f.year_built_max;
  if (f.neighborhoods?.length) body.neighborhoods = f.neighborhoods;
  if (f.streets?.length) body.streets = f.streets;
  if (f.condition?.length) body.condition = f.condition;
  if (f.building_status?.length) body.building_status = f.building_status;
  if (f.project_type?.length) body.project_type = f.project_type;
  if (f.exclude_agency) body.exclude_agency = true;
  if (f.exclude_no_agents) body.exclude_forbids_agents = true;
  if (f.exclude_exclusive) body.exclude_exclusive = true;
  if (f.placed_by_us) body.placed_by_us = f.placed_by_us;
  return body;
}

export function hasAnyFilter(f: Filters): boolean {
  return !!(
    f.deal_type ||
    f.property_type ||
    f.platform ||
    f.rooms_pick?.length ||
    f.bedrooms_pick?.length ||
    f.bathrooms_pick?.length ||
    f.price_min_usd != null ||
    f.price_max_usd != null ||
    f.area_min_sqm != null ||
    f.area_max_sqm != null ||
    f.floor_min != null ||
    f.floor_max != null ||
    f.total_floors_min != null ||
    f.total_floors_max != null ||
    f.year_built_min != null ||
    f.year_built_max != null ||
    f.neighborhoods?.length ||
    f.streets?.length ||
    f.condition?.length ||
    f.building_status?.length ||
    f.project_type?.length ||
    f.exclude_agency ||
    f.exclude_no_agents ||
    f.exclude_exclusive ||
    f.placed_by_us ||
    (f.sort && f.sort !== "newest")
  );
}

function FilterGroupDivider() {
  return (
    <span
      aria-hidden
      className="hidden sm:inline-block w-px h-5 bg-white/10 mx-0.5"
    />
  );
}

/** Counts only the *advanced* axes — what's reachable through this popover.
 *  Used for the "More · N" badge so users can see at a glance that
 *  collapsed filters are active. Each axis (e.g. floor min/max) counts
 *  once regardless of how many sub-fields back it. */
function countAdvancedActive(f: Filters): number {
  let n = 0;
  if (f.bathrooms_pick?.length) n++;
  if (f.floor_min != null || f.floor_max != null) n++;
  if (f.total_floors_min != null || f.total_floors_max != null) n++;
  if (f.year_built_min != null || f.year_built_max != null) n++;
  if (f.project_type?.length) n++;
  if (f.platform) n++;
  return n;
}

function MoreFiltersDropdown({
  filters,
  onPatch,
}: {
  filters: Filters;
  onPatch: (p: Partial<Filters>) => void;
}) {
  const [open, setOpen] = useState(false);
  const activeCount = countAdvancedActive(filters);
  const label = activeCount > 0 ? `More · ${activeCount}` : "More filters";

  const clearAdvanced = () =>
    onPatch({
      bathrooms_pick: undefined,
      floor_min: undefined,
      floor_max: undefined,
      total_floors_min: undefined,
      total_floors_max: undefined,
      year_built_min: undefined,
      year_built_max: undefined,
      project_type: undefined,
      platform: undefined,
    });

  return (
    <div className="relative">
      <TriggerButton
        active={activeCount > 0}
        open={open}
        onClick={() => setOpen((o) => !o)}
      >
        {label}
      </TriggerButton>
      <Popover open={open} onClose={() => setOpen(false)} align="right">
        <div className="w-[24rem] sm:w-[26rem] max-h-[34rem] overflow-y-auto">
          <div className="space-y-3.5">
            <FormSection title="Bathrooms">
              <PillCountRow
                value={filters.bathrooms_pick}
                onChange={(v) => onPatch({ bathrooms_pick: v })}
                max={4}
              />
            </FormSection>

            <FormSection title="Floor in building">
              <InlineRange
                minValue={filters.floor_min}
                maxValue={filters.floor_max}
                onChange={(min, max) =>
                  onPatch({ floor_min: min, floor_max: max })
                }
                inputMin={0}
                inputMax={200}
                presets={[
                  { label: "Ground (0)", min: 0, max: 0 },
                  { label: "1 – 3", min: 1, max: 3 },
                  { label: "4 – 9", min: 4, max: 9 },
                  { label: "10+", min: 10, max: undefined },
                ]}
              />
            </FormSection>

            <FormSection title="Building stories">
              <InlineRange
                minValue={filters.total_floors_min}
                maxValue={filters.total_floors_max}
                onChange={(min, max) =>
                  onPatch({ total_floors_min: min, total_floors_max: max })
                }
                inputMin={1}
                inputMax={200}
                presets={[
                  { label: "Low-rise (≤ 5)", min: undefined, max: 5 },
                  { label: "Mid-rise (6 – 12)", min: 6, max: 12 },
                  { label: "High-rise (13+)", min: 13, max: undefined },
                ]}
              />
            </FormSection>

            <FormSection title="Year built">
              <InlineRange
                minValue={filters.year_built_min}
                maxValue={filters.year_built_max}
                onChange={(min, max) =>
                  onPatch({ year_built_min: min, year_built_max: max })
                }
                inputMin={1800}
                inputMax={2100}
                presets={[
                  { label: "After 2020", min: 2020, max: undefined },
                  { label: "2010 – 2019", min: 2010, max: 2019 },
                  { label: "2000 – 2009", min: 2000, max: 2009 },
                  { label: "Before 2000", min: undefined, max: 1999 },
                ]}
              />
            </FormSection>

            <FormSection title="Project type">
              <PillEnumGrid
                options={PROJECT_TYPE_OPTIONS}
                value={filters.project_type}
                onChange={(v) => onPatch({ project_type: v })}
              />
            </FormSection>

            <FormSection title="Source portal">
              <PillSingleEnumRow
                options={PLATFORM_OPTIONS}
                value={filters.platform}
                onChange={(v) => onPatch({ platform: v })}
                anyLabel="Any"
              />
            </FormSection>
          </div>

          {activeCount > 0 && (
            <div className="flex justify-end pt-2 mt-3 border-t border-white/5 sticky bottom-0 bg-ink-900">
              <button
                type="button"
                onClick={clearAdvanced}
                className="text-[11px] text-slate-500 hover:text-slate-300"
              >
                Clear advanced
              </button>
            </div>
          )}
        </div>
      </Popover>
    </div>
  );
}

/** Section header + body wrapper for the More-filters panel. Keeps spacing
 *  consistent across heterogeneous controls (pills, inputs, grids). */
function FormSection({
  title,
  children,
}: {
  title: string;
  children: React.ReactNode;
}) {
  return (
    <div>
      <div className="text-[10px] font-semibold uppercase tracking-wider text-slate-500 mb-1.5 px-0.5">
        {title}
      </div>
      {children}
    </div>
  );
}

/** Multi-pick count row for [1..max], top button rendered as ``${max}+``.
 *  Used inline (no popover wrapper) so it can live inside the More panel. */
function PillCountRow({
  value,
  onChange,
  max,
}: {
  value: number[] | undefined;
  onChange: (v: number[] | undefined) => void;
  max: number;
}) {
  const picks = value || [];
  const cells = Array.from({ length: max }, (_, i) => i + 1);
  const toggle = (n: number) => {
    const set = new Set(picks);
    if (set.has(n)) set.delete(n);
    else set.add(n);
    onChange(set.size ? Array.from(set).sort((a, b) => a - b) : undefined);
  };
  return (
    <div
      className="grid gap-1"
      style={{ gridTemplateColumns: `repeat(${max}, minmax(0, 1fr))` }}
    >
      {cells.map((n) => (
        <button
          key={n}
          type="button"
          onClick={() => toggle(n)}
          className={`h-9 rounded-md text-[12px] font-medium transition-colors ${
            picks.includes(n)
              ? "bg-accent-500/20 text-accent-100 ring-1 ring-accent-500/40"
              : "bg-ink-850 text-slate-300 hover:bg-white/[0.06]"
          }`}
        >
          {n === max ? `${max}+` : n}
        </button>
      ))}
    </div>
  );
}

/** Inline min/max number inputs + optional preset chips. Commits on blur
 *  and on Enter; the parent owns the values, this only stages text. */
function InlineRange({
  minValue,
  maxValue,
  onChange,
  inputMin = 0,
  inputMax,
  presets,
}: {
  minValue?: number;
  maxValue?: number;
  onChange: (min: number | undefined, max: number | undefined) => void;
  inputMin?: number;
  inputMax?: number;
  presets?: { label: string; min?: number; max?: number }[];
}) {
  const [localMin, setLocalMin] = useState<string>(
    minValue != null ? String(minValue) : ""
  );
  const [localMax, setLocalMax] = useState<string>(
    maxValue != null ? String(maxValue) : ""
  );
  useEffect(() => {
    setLocalMin(minValue != null ? String(minValue) : "");
    setLocalMax(maxValue != null ? String(maxValue) : "");
  }, [minValue, maxValue]);

  const sanitize = (s: string): number | undefined => {
    if (s === "") return undefined;
    const n = Number(s);
    if (!Number.isFinite(n)) return undefined;
    if (n < inputMin) return undefined;
    if (inputMax != null && n > inputMax) return undefined;
    return n;
  };

  const commit = () => onChange(sanitize(localMin), sanitize(localMax));

  return (
    <div className="space-y-2">
      <div className="grid grid-cols-2 gap-2">
        <input
          type="number"
          inputMode="numeric"
          min={inputMin}
          max={inputMax}
          placeholder="any"
          value={localMin}
          onChange={(e) => setLocalMin(e.target.value)}
          onBlur={commit}
          onKeyDown={(e) => {
            if (e.key === "Enter") commit();
          }}
          className="block w-full rounded-md bg-ink-850 border border-white/10 px-2.5 py-1.5 text-[12px] text-slate-100 focus:border-accent-500/40 focus:outline-none"
          aria-label="Minimum"
        />
        <input
          type="number"
          inputMode="numeric"
          min={inputMin}
          max={inputMax}
          placeholder="any"
          value={localMax}
          onChange={(e) => setLocalMax(e.target.value)}
          onBlur={commit}
          onKeyDown={(e) => {
            if (e.key === "Enter") commit();
          }}
          className="block w-full rounded-md bg-ink-850 border border-white/10 px-2.5 py-1.5 text-[12px] text-slate-100 focus:border-accent-500/40 focus:outline-none"
          aria-label="Maximum"
        />
      </div>
      {presets && presets.length > 0 && (
        <div className="grid grid-cols-2 gap-1">
          {presets.map((r) => (
            <button
              key={r.label}
              type="button"
              onClick={() => {
                setLocalMin(r.min != null ? String(r.min) : "");
                setLocalMax(r.max != null ? String(r.max) : "");
                onChange(r.min, r.max);
              }}
              className="text-left text-[11px] text-slate-300 hover:bg-white/[0.04] hover:text-slate-100 px-2 py-1.5 rounded-md border border-white/5 hover:border-white/15 transition-colors"
            >
              {r.label}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

/** Wrap-friendly pill grid for multi-select enums. The label is the
 *  Georgian primary, the English gloss sits underneath in muted type — same
 *  layout as the BUILDING_STATUS / CONDITION trigger popovers. */
function PillEnumGrid({
  options,
  value,
  onChange,
}: {
  options: { value: string; label: string; en?: string }[];
  value: string[] | undefined;
  onChange: (v: string[] | undefined) => void;
}) {
  const picks = new Set(value || []);
  const toggle = (v: string) => {
    const set = new Set(picks);
    if (set.has(v)) set.delete(v);
    else set.add(v);
    onChange(set.size ? Array.from(set) : undefined);
  };
  return (
    <div className="flex flex-wrap gap-1">
      {options.map((o) => {
        const on = picks.has(o.value);
        return (
          <button
            key={o.value}
            type="button"
            onClick={() => toggle(o.value)}
            className={`px-2 py-1 rounded-md text-[11px] transition-colors ${
              on
                ? "bg-accent-500/20 text-accent-100 ring-1 ring-accent-500/40"
                : "bg-ink-850 text-slate-300 hover:bg-white/[0.06]"
            }`}
            title={o.en}
          >
            {o.label}
          </button>
        );
      })}
    </div>
  );
}

/** Single-pick row with an "any" sentinel at the front. Falls through to
 *  ``undefined`` when the same option is clicked twice (toggle off). */
function PillSingleEnumRow<T extends string>({
  options,
  value,
  onChange,
  anyLabel,
}: {
  options: { value: T; label: string }[];
  value: T | undefined;
  onChange: (v: T | undefined) => void;
  anyLabel: string;
}) {
  const cls = (on: boolean) =>
    `px-2.5 py-1 rounded-md text-[11px] transition-colors ${
      on
        ? "bg-accent-500/20 text-accent-100 ring-1 ring-accent-500/40"
        : "bg-ink-850 text-slate-300 hover:bg-white/[0.06]"
    }`;
  return (
    <div className="flex flex-wrap gap-1">
      <button
        type="button"
        onClick={() => onChange(undefined)}
        className={cls(!value)}
      >
        {anyLabel}
      </button>
      {options.map((o) => {
        const on = value === o.value;
        return (
          <button
            key={o.value}
            type="button"
            onClick={() => onChange(on ? undefined : o.value)}
            className={cls(on)}
          >
            {o.label}
          </button>
        );
      })}
    </div>
  );
}

export function FilterBar({
  filters,
  onChange,
  hideOwnerDirect = false,
}: {
  filters: Filters;
  onChange: (next: Filters) => void;
  /** Hot Offers always runs as if all three exclusions are on (the route
   *  applies them unconditionally), so the toggles there would be no-ops.
   *  Hide them on that page to avoid misleading controls. */
  hideOwnerDirect?: boolean;
}) {
  const patch = (p: Partial<Filters>) => onChange({ ...filters, ...p });
  const clearAll = () => onChange({});
  const anyActive = hasAnyFilter(filters);

  // The bar groups filters by intent so wrapping doesn't split a logical
  // group across rows. Primary identifiers (deal/type/rooms…) come first,
  // then money/size, then location/condition, then everything rarer behind
  // a "More" popover. Sort + reset always pin to the right.
  return (
    <div className="flex flex-wrap items-center gap-x-1.5 gap-y-2">
      {/* Group 1 — what & how big */}
      <div className="flex flex-wrap items-center gap-1.5">
        <DealDropdown value={filters.deal_type} onChange={(v) => patch({ deal_type: v })} />
        <PropertyDropdown
          value={filters.property_type}
          onChange={(v) => patch({ property_type: v })}
        />
        <RoomsDropdown
          value={filters.rooms_pick}
          onChange={(v) => patch({ rooms_pick: v })}
        />
        <BedroomsDropdown
          value={filters.bedrooms_pick}
          onChange={(v) => patch({ bedrooms_pick: v })}
        />
      </div>

      <FilterGroupDivider />

      {/* Group 2 — money & size */}
      <div className="flex flex-wrap items-center gap-1.5">
        <PriceDropdown
          min={filters.price_min_usd}
          max={filters.price_max_usd}
          currency={filters.price_currency ?? "USD"}
          onChange={(min, max, currency) =>
            patch({
              price_min_usd: min,
              price_max_usd: max,
              price_currency: currency,
            })
          }
        />
        <AreaDropdown
          min={filters.area_min_sqm}
          max={filters.area_max_sqm}
          onChange={(min, max) => patch({ area_min_sqm: min, area_max_sqm: max })}
        />
      </div>

      <FilterGroupDivider />

      {/* Group 3 — where & how built */}
      <div className="flex flex-wrap items-center gap-1.5">
        <NeighborhoodDropdown
          value={filters.neighborhoods}
          onChange={(v) => patch({ neighborhoods: v })}
          streetValue={filters.streets}
          onStreetsChange={(v) => patch({ streets: v })}
        />
        <BuildingStatusDropdown
          value={filters.building_status}
          onChange={(v) => patch({ building_status: v })}
        />
        <ConditionDropdown
          value={filters.condition}
          onChange={(v) => patch({ condition: v })}
        />
        <MoreFiltersDropdown filters={filters} onPatch={patch} />
      </div>

      {/* Right cluster — sort + global affordances. ``ml-auto`` plus a
          full-width fallback below the sm breakpoint so on phones the
          cluster simply stacks underneath instead of fighting for space. */}
      <div className="w-full sm:w-auto sm:ml-auto flex items-center flex-wrap gap-1.5">
        <PlacedByUsDropdown
          value={filters.placed_by_us}
          onChange={(v) => patch({ placed_by_us: v })}
        />
        {!hideOwnerDirect && (
          <SourceDropdown filters={filters} onPatch={patch} />
        )}
        <SortDropdown
          value={filters.sort || "newest"}
          onChange={(v) => patch({ sort: v === "newest" ? undefined : v })}
        />
        {anyActive && (
          <button
            type="button"
            onClick={clearAll}
            className="text-[11px] text-slate-500 hover:text-slate-300 px-2 py-1.5 whitespace-nowrap"
          >
            Clear all
          </button>
        )}
      </div>
    </div>
  );
}

// ─── Shared dropdown primitives ────────────────────────────────────────────

/** The pill-shaped trigger button. Active state = accent background so the
 *  user spots applied filters at a glance. ``wide`` reserves room for a
 *  longer label (e.g. "$100k–$300k"); default is compact. */
function TriggerButton({
  active,
  onClick,
  children,
  open,
}: {
  active: boolean;
  onClick: () => void;
  children: React.ReactNode;
  open?: boolean;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`inline-flex items-center gap-1.5 h-9 px-3 rounded-lg text-[12px] font-medium whitespace-nowrap transition-colors ${
        active
          ? "bg-accent-500/15 text-accent-100 border border-accent-500/40 shadow-sm shadow-accent-500/10"
          : "bg-ink-900/60 text-slate-300 border border-white/10 hover:border-white/20 hover:text-slate-100"
      }`}
    >
      <span>{children}</span>
      <svg
        width="10"
        height="10"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2.5"
        strokeLinecap="round"
        strokeLinejoin="round"
        className={`transition-transform ${open ? "rotate-180" : "rotate-0"} ${
          active ? "opacity-80" : "opacity-50"
        }`}
      >
        <polyline points="6 9 12 15 18 9" />
      </svg>
    </button>
  );
}

/** Generic popover wrapper. Click the trigger to open; click outside or
 *  press Escape to close. Align left by default. */
function Popover({
  open,
  onClose,
  children,
  align = "left",
}: {
  open: boolean;
  onClose: () => void;
  children: React.ReactNode;
  align?: "left" | "right";
}) {
  useEffect(() => {
    if (!open) return;
    const onEsc = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onEsc);
    return () => window.removeEventListener("keydown", onEsc);
  }, [open, onClose]);

  if (!open) return null;
  // High z-index so the dropdown and its click-outside scrim sit above the
  // rest of the page chrome (sticky headers, floating panels).
  return (
    <>
      <div className="fixed inset-0 z-[1100]" onClick={onClose} />
      <div
        className={`absolute z-[1200] mt-1 ${
          align === "right" ? "right-0" : "left-0"
        } min-w-[14rem] rounded-lg bg-ink-900 border border-white/10 shadow-xl p-3`}
      >
        {children}
      </div>
    </>
  );
}

/** One option button inside a popover. Compact, visible "checked" state,
 *  scales to both radio-style (single) and checkbox-style (multi) usage. */
function MenuItem({
  checked,
  onClick,
  children,
}: {
  checked: boolean;
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`w-full flex items-center justify-between gap-2 px-2.5 py-1.5 rounded-md text-[12px] text-left transition-colors ${
        checked
          ? "bg-accent-500/15 text-accent-100"
          : "text-slate-300 hover:bg-white/[0.04] hover:text-slate-100"
      }`}
    >
      <span>{children}</span>
      {checked && (
        <svg
          width="12"
          height="12"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="3"
          strokeLinecap="round"
          strokeLinejoin="round"
          className="text-accent-300"
        >
          <polyline points="20 6 9 17 4 12" />
        </svg>
      )}
    </button>
  );
}

// ─── Individual filter dropdowns ───────────────────────────────────────────

function DealDropdown({
  value,
  onChange,
}: {
  value: DealType | undefined;
  onChange: (v: DealType | undefined) => void;
}) {
  const [open, setOpen] = useState(false);
  const label = value === "sale" ? "Sale" : value === "rent" ? "Rent" : "Deal";
  return (
    <div className="relative">
      <TriggerButton active={!!value} open={open} onClick={() => setOpen((o) => !o)}>
        {label}
      </TriggerButton>
      <Popover open={open} onClose={() => setOpen(false)}>
        <div className="flex flex-col gap-0.5 min-w-[10rem]">
          <MenuItem checked={!value} onClick={() => { onChange(undefined); setOpen(false); }}>
            Any deal
          </MenuItem>
          <MenuItem checked={value === "sale"} onClick={() => { onChange("sale"); setOpen(false); }}>
            Sale
          </MenuItem>
          <MenuItem checked={value === "rent"} onClick={() => { onChange("rent"); setOpen(false); }}>
            Rent
          </MenuItem>
        </div>
      </Popover>
    </div>
  );
}

function PropertyDropdown({
  value,
  onChange,
}: {
  value: PropertyType | undefined;
  onChange: (v: PropertyType | undefined) => void;
}) {
  const [open, setOpen] = useState(false);
  const label = value
    ? PROPERTY_OPTIONS.find((o) => o.value === value)?.label ?? value
    : "Type";
  return (
    <div className="relative">
      <TriggerButton active={!!value} open={open} onClick={() => setOpen((o) => !o)}>
        {label}
      </TriggerButton>
      <Popover open={open} onClose={() => setOpen(false)}>
        <div className="flex flex-col gap-0.5 min-w-[12rem]">
          <MenuItem checked={!value} onClick={() => { onChange(undefined); setOpen(false); }}>
            Any type
          </MenuItem>
          {PROPERTY_OPTIONS.map((o) => (
            <MenuItem
              key={o.value}
              checked={value === o.value}
              onClick={() => {
                onChange(value === o.value ? undefined : o.value);
                setOpen(false);
              }}
            >
              {o.label}
            </MenuItem>
          ))}
        </div>
      </Popover>
    </div>
  );
}

function RoomsDropdown(props: {
  value: number[] | undefined;
  onChange: (v: number[] | undefined) => void;
}) {
  return (
    <CountPicker
      {...props}
      name="Rooms"
      pluralName="rooms"
      sectionLabel="Room count"
    />
  );
}

function BedroomsDropdown(props: {
  value: number[] | undefined;
  onChange: (v: number[] | undefined) => void;
}) {
  return (
    <CountPicker
      {...props}
      name="Bedrooms"
      pluralName="bedrooms"
      sectionLabel="Bedroom count"
    />
  );
}

/** Shared implementation for any "pick one or more N-count" filter. Both
 *  Rooms and Bedrooms have identical semantics — multi-select pills over
 *  [1..5], where 5 means "5+" (no upper bound) — so the markup lives in
 *  one place and the two widgets only differ in their labels. */
function CountPicker({
  value,
  onChange,
  name,
  pluralName,
  sectionLabel,
  max = 5,
}: {
  value: number[] | undefined;
  onChange: (v: number[] | undefined) => void;
  name: string;
  pluralName: string;
  sectionLabel: string;
  /** Top of the range; the top button always renders as "N+" (no upper
   *  bound). Defaults to 5. */
  max?: number;
}) {
  const [open, setOpen] = useState(false);
  const picks = value || [];
  let label: string = name;
  if (picks.length === 1) label = `${picks[0] === max ? `${max}+` : picks[0]} ${pluralName}`;
  else if (picks.length > 1) {
    const sorted = [...picks].sort((a, b) => a - b);
    const hi = sorted[sorted.length - 1] === max ? `${max}+` : String(sorted[sorted.length - 1]);
    label = `${sorted[0]}–${hi} ${pluralName}`;
  }

  const toggle = (n: number) => {
    const set = new Set(picks);
    if (set.has(n)) set.delete(n);
    else set.add(n);
    onChange(set.size ? Array.from(set).sort((a, b) => a - b) : undefined);
  };

  const cells = Array.from({ length: max }, (_, i) => i + 1);
  return (
    <div className="relative">
      <TriggerButton active={picks.length > 0} open={open} onClick={() => setOpen((o) => !o)}>
        {label}
      </TriggerButton>
      <Popover open={open} onClose={() => setOpen(false)}>
        <div className="min-w-[13rem] space-y-2">
          <div className="text-[10px] font-semibold uppercase tracking-wider text-slate-500 px-1">
            {sectionLabel}
          </div>
          <div
            className="grid gap-1"
            style={{ gridTemplateColumns: `repeat(${max}, minmax(0, 1fr))` }}
          >
            {cells.map((n) => (
              <button
                key={n}
                type="button"
                onClick={() => toggle(n)}
                className={`h-9 rounded-md text-[12px] font-medium transition-colors ${
                  picks.includes(n)
                    ? "bg-accent-500/20 text-accent-100 ring-1 ring-accent-500/40"
                    : "bg-ink-850 text-slate-300 hover:bg-white/[0.06]"
                }`}
              >
                {n === max ? `${max}+` : n}
              </button>
            ))}
          </div>
          {picks.length > 0 && (
            <div className="flex justify-end pt-1 border-t border-white/5">
              <button
                type="button"
                onClick={() => { onChange(undefined); setOpen(false); }}
                className="text-[11px] text-slate-500 hover:text-slate-300"
              >
                Clear
              </button>
            </div>
          )}
        </div>
      </Popover>
    </div>
  );
}

type PriceCurrency = "USD" | "GEL";

/** Quick-pick buckets per currency. The lari ones aren't the dollar ones
 *  converted — they're the round numbers a Georgian broker actually thinks
 *  in, which is the point of offering the currency at all. */
const PRICE_QUICK_PICKS: Record<
  PriceCurrency,
  { label: string; min?: number; max?: number }[]
> = {
  USD: [
    { label: "Under $100k", max: 100000 },
    { label: "$100k – $300k", min: 100000, max: 300000 },
    { label: "$300k – $600k", min: 300000, max: 600000 },
    { label: "$600k+", min: 600000 },
  ],
  GEL: [
    { label: "≤ 1,000 ₾", max: 1000 },
    { label: "1,000 – 3,000 ₾", min: 1000, max: 3000 },
    { label: "3,000 – 10,000 ₾", min: 3000, max: 10000 },
    { label: "10,000 ₾+", min: 10000 },
  ],
};

function PriceDropdown({
  min,
  max,
  currency,
  onChange,
}: {
  min?: number;
  max?: number;
  currency: PriceCurrency;
  onChange: (
    min: number | undefined,
    max: number | undefined,
    currency: PriceCurrency,
  ) => void;
}) {
  const [open, setOpen] = useState(false);
  const [localMin, setLocalMin] = useState<string>(min != null ? String(min) : "");
  const [localMax, setLocalMax] = useState<string>(max != null ? String(max) : "");

  useEffect(() => {
    setLocalMin(min != null ? String(min) : "");
    setLocalMax(max != null ? String(max) : "");
  }, [min, max]);

  const sym = currency === "GEL" ? "₾" : "$";
  const fmt = (n: number) =>
    n >= 1000
      ? `${sym}${Math.round(n / 1000)}k`
      : `${sym}${n.toLocaleString()}`;
  const label = useMemo(() => {
    if (min != null && max != null) return `${fmt(min)}–${fmt(max)}`;
    if (max != null) return `≤ ${fmt(max)}`;
    if (min != null) return `≥ ${fmt(min)}`;
    return "Price";
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [min, max, currency]);

  const commit = (closeAfter = true, nextCurrency: PriceCurrency = currency) => {
    const parsedMin = localMin === "" ? undefined : Number(localMin);
    const parsedMax = localMax === "" ? undefined : Number(localMax);
    onChange(
      Number.isFinite(parsedMin) ? (parsedMin as number) : undefined,
      Number.isFinite(parsedMax) ? (parsedMax as number) : undefined,
      nextCurrency,
    );
    if (closeAfter) setOpen(false);
  };

  return (
    <div className="relative">
      <TriggerButton
        active={min != null || max != null}
        open={open}
        onClick={() => setOpen((o) => !o)}
      >
        {label}
      </TriggerButton>
      <Popover
        open={open}
        onClose={() => {
          commit(false);
          setOpen(false);
        }}
      >
        <div className="min-w-[16rem] space-y-2.5">
          {/* Switching currency keeps whatever is typed — the numbers mean
              a different thing now, which is what the broker intends when
              they retype "3000" as lari rather than dollars. */}
          <div className="flex items-center gap-1 rounded-md bg-ink-850 border border-white/10 p-0.5">
            {(["USD", "GEL"] as PriceCurrency[]).map((c) => (
              <button
                key={c}
                type="button"
                onClick={() => commit(false, c)}
                className={`flex-1 rounded px-2 py-1 text-[11.5px] font-medium transition-colors ${
                  currency === c
                    ? "bg-accent-500/20 text-accent-200"
                    : "text-slate-400 hover:text-slate-200"
                }`}
              >
                {c === "GEL" ? "₾ GEL" : "$ USD"}
              </button>
            ))}
          </div>
          <div className="grid grid-cols-2 gap-2">
            <label className="block">
              <span className="block text-[10px] font-semibold uppercase tracking-wider text-slate-500 mb-1">
                Min
              </span>
              <div className="relative">
                <span className="absolute left-2 top-1/2 -translate-y-1/2 text-[12px] text-slate-500">{sym}</span>
                <input
                  type="number"
                  inputMode="numeric"
                  placeholder="0"
                  value={localMin}
                  onChange={(e) => setLocalMin(e.target.value)}
                  onKeyDown={(e) => { if (e.key === "Enter") commit(); }}
                  className="block w-full rounded-md bg-ink-850 border border-white/10 pl-5 pr-2 py-1.5 text-[12px] text-slate-100 focus:border-accent-500/40 focus:outline-none"
                />
              </div>
            </label>
            <label className="block">
              <span className="block text-[10px] font-semibold uppercase tracking-wider text-slate-500 mb-1">
                Max
              </span>
              <div className="relative">
                <span className="absolute left-2 top-1/2 -translate-y-1/2 text-[12px] text-slate-500">{sym}</span>
                <input
                  type="number"
                  inputMode="numeric"
                  placeholder="any"
                  value={localMax}
                  onChange={(e) => setLocalMax(e.target.value)}
                  onKeyDown={(e) => { if (e.key === "Enter") commit(); }}
                  className="block w-full rounded-md bg-ink-850 border border-white/10 pl-5 pr-2 py-1.5 text-[12px] text-slate-100 focus:border-accent-500/40 focus:outline-none"
                />
              </div>
            </label>
          </div>
          {/* Quick-pick ranges — common buckets so agents don't have to type. */}
          <div>
            <div className="text-[10px] font-semibold uppercase tracking-wider text-slate-500 mb-1">
              Quick picks
            </div>
            <div className="grid grid-cols-2 gap-1">
              {PRICE_QUICK_PICKS[currency].map((r) => (
                <button
                  key={r.label}
                  type="button"
                  onClick={() => {
                    setLocalMin(r.min != null ? String(r.min) : "");
                    setLocalMax(r.max != null ? String(r.max) : "");
                    onChange(r.min, r.max, currency);
                    setOpen(false);
                  }}
                  className="text-[11px] px-2 py-1.5 rounded-md bg-ink-850 hover:bg-white/[0.06] border border-white/5 text-slate-300 text-center"
                >
                  {r.label}
                </button>
              ))}
            </div>
          </div>
          <div className="flex justify-between pt-2 border-t border-white/5">
            <button
              type="button"
              onClick={() => {
                setLocalMin("");
                setLocalMax("");
                onChange(undefined, undefined, currency);
                setOpen(false);
              }}
              className="text-[11px] text-slate-500 hover:text-slate-300"
            >
              Clear
            </button>
            <button
              type="button"
              onClick={() => commit()}
              className="text-[11px] font-semibold text-accent-300 hover:text-accent-200"
            >
              Apply
            </button>
          </div>
        </div>
      </Popover>
    </div>
  );
}

// Catalog row types — mirror /api/catalog/locations response. Used by
// the NeighborhoodDropdown to lazy-load streets per urban.
interface CatalogStreetNode {
  id: string;
  name: string;
  name_normalized: string;
}
interface CatalogUrbanNode {
  name: string;
  name_normalized: string;
  canonical_code: string | null;
  streets: CatalogStreetNode[];
}
interface CatalogDistrictNode {
  name: string;
  name_normalized: string;
  urbans: CatalogUrbanNode[];
}

/** Neighborhood + street picker. Outer multi-select is the same set of 26
 *  fine-grained neighborhoods grouped by macro-region (labels in Georgian).
 *  Each neighborhood can be expanded to reveal its catalog streets — those
 *  streets become an independent filter dimension (backend ILIKEs against
 *  ``HotOffer.street``). The two dimensions AND together server-side, so
 *  picking ``Vake`` + a Vake street narrows to "Vake listings whose street
 *  matches X".
 *
 *  Trigger label shows the neighborhood pick count, plus a `+N streets`
 *  badge when any streets are selected, so the toolbar stays scannable. */
function NeighborhoodDropdown({
  value,
  onChange,
  streetValue,
  onStreetsChange,
}: {
  value: string[] | undefined;
  onChange: (v: string[] | undefined) => void;
  streetValue: string[] | undefined;
  onStreetsChange: (v: string[] | undefined) => void;
}) {
  const [open, setOpen] = useState(false);
  const picks = value || [];
  const picksSet = useMemo(() => new Set(picks), [picks]);
  const streetPicks = streetValue || [];
  const streetPicksSet = useMemo(() => new Set(streetPicks), [streetPicks]);

  // Streets-per-urban only matters once the user opens the popover, so we
  // lazy-load the catalog on first open. Cached for the life of the
  // component (~1MB JSON, rarely changes).
  const [catalog, setCatalog] = useState<CatalogDistrictNode[] | null>(null);
  useEffect(() => {
    if (!open || catalog !== null) return;
    let cancelled = false;
    (async () => {
      try {
        const r = await api<{ districts: CatalogDistrictNode[] }>(
          "/api/catalog/locations",
        );
        if (!cancelled) setCatalog(r.districts);
      } catch {
        if (!cancelled) setCatalog([]);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [open, catalog]);

  // Map slug -> the catalog urban node carrying that canonical_code so a
  // neighborhood checkbox click can fan out to its streets without a
  // second pass over the tree.
  const urbanBySlug = useMemo(() => {
    const m = new Map<string, CatalogUrbanNode>();
    for (const d of catalog ?? []) {
      for (const u of d.urbans) {
        if (u.canonical_code) m.set(u.canonical_code, u);
      }
    }
    return m;
  }, [catalog]);

  let label = "Neighborhood";
  if (picks.length === 1) label = NEIGHBORHOOD_LABELS_KA[picks[0]] ?? picks[0];
  else if (picks.length > 1) label = `${picks.length} neighborhoods`;
  if (streetPicks.length > 0) {
    label = `${label} +${streetPicks.length} street${streetPicks.length === 1 ? "" : "s"}`;
  }

  const toggle = (v: string) => {
    const set = new Set(picks);
    if (set.has(v)) set.delete(v);
    else set.add(v);
    onChange(set.size ? Array.from(set) : undefined);
  };
  const toggleGroup = (groupItems: { value: string }[]) => {
    const set = new Set(picks);
    const allIn = groupItems.every((i) => set.has(i.value));
    if (allIn) groupItems.forEach((i) => set.delete(i.value));
    else groupItems.forEach((i) => set.add(i.value));
    onChange(set.size ? Array.from(set) : undefined);
  };

  // Which neighborhood rows have their nested street list expanded. Reset
  // is deliberate — auto-expanding picked rows would jolt the layout when
  // the user just wants to add another neighborhood without seeing streets.
  const [expandedSlugs, setExpandedSlugs] = useState<Set<string>>(new Set());
  const toggleExpand = (slug: string) =>
    setExpandedSlugs((prev) => {
      const next = new Set(prev);
      if (next.has(slug)) next.delete(slug);
      else next.add(slug);
      return next;
    });

  const toggleStreet = (token: string) => {
    const set = new Set(streetPicks);
    if (set.has(token)) set.delete(token);
    else set.add(token);
    onStreetsChange(set.size ? Array.from(set) : undefined);
  };

  return (
    <div className="relative">
      <TriggerButton
        active={picks.length > 0 || streetPicks.length > 0}
        open={open}
        onClick={() => setOpen((o) => !o)}
      >
        {label}
      </TriggerButton>
      <Popover open={open} onClose={() => setOpen(false)}>
        <div className="w-[24rem] max-h-[32rem] overflow-y-auto">
          <div className="space-y-3">
            {NEIGHBORHOOD_GROUPS.map((g) => {
              const allIn = g.items.every((i) => picksSet.has(i.value));
              const someIn = !allIn && g.items.some((i) => picksSet.has(i.value));
              return (
                <div key={g.adminCode}>
                  <button
                    type="button"
                    onClick={() => toggleGroup(g.items)}
                    className="w-full flex items-center justify-between text-[11px] font-semibold text-slate-400 hover:text-slate-200 px-1 pb-1 mb-1 border-b border-white/5 transition-colors"
                  >
                    <span>{g.labelKa}</span>
                    <span className="font-medium text-[10px] text-slate-500">
                      {allIn ? "clear all" : someIn ? "select rest" : "select all"}
                    </span>
                  </button>
                  <div className="flex flex-col gap-0.5">
                    {g.items.map((i) => {
                      const urban = urbanBySlug.get(i.value);
                      const streetCount = urban?.streets.length ?? 0;
                      const isExpanded = expandedSlugs.has(i.value);
                      const pickedHere = urban
                        ? urban.streets.filter((s) =>
                            streetPicksSet.has(s.id),
                          ).length
                        : 0;
                      return (
                        <div key={i.value}>
                          <div className="flex items-center gap-1">
                            <div className="flex-1 min-w-0">
                              <MenuItem
                                checked={picksSet.has(i.value)}
                                onClick={() => toggle(i.value)}
                              >
                                <span className="flex items-center gap-1.5">
                                  <span>{i.labelKa}</span>
                                  {pickedHere > 0 && (
                                    <span className="text-[10px] text-accent-300/80">
                                      · {pickedHere}
                                    </span>
                                  )}
                                </span>
                              </MenuItem>
                            </div>
                            {streetCount > 0 && (
                              <button
                                type="button"
                                onClick={() => toggleExpand(i.value)}
                                title={
                                  isExpanded
                                    ? "Hide streets"
                                    : `Show ${streetCount} streets`
                                }
                                className="text-[10px] text-slate-500 hover:text-slate-300 px-1.5 py-1 rounded inline-flex items-center gap-1"
                              >
                                {streetCount}
                                <svg
                                  width="9"
                                  height="9"
                                  viewBox="0 0 24 24"
                                  fill="none"
                                  stroke="currentColor"
                                  strokeWidth="2.5"
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                  className={isExpanded ? "rotate-180" : ""}
                                >
                                  <polyline points="6 9 12 15 18 9" />
                                </svg>
                              </button>
                            )}
                          </div>
                          {isExpanded && urban && (
                            <div className="ml-5 mt-0.5 mb-1 border-l border-white/10 pl-2">
                              {urban.streets.length === 0 ? (
                                <div className="text-[11px] text-slate-600 italic py-1">
                                  no streets in catalog
                                </div>
                              ) : (
                                <div className="grid grid-cols-1 gap-0.5 max-h-48 overflow-y-auto">
                                  {urban.streets.map((s) => (
                                    <MenuItem
                                      key={s.id}
                                      checked={streetPicksSet.has(s.id)}
                                      onClick={() => toggleStreet(s.id)}
                                    >
                                      <span className="text-[11px]">
                                        {s.name}
                                      </span>
                                    </MenuItem>
                                  ))}
                                </div>
                              )}
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>
              );
            })}
            {catalog === null && (
              <div className="text-[11px] text-slate-500 italic px-1 pt-1">
                loading streets…
              </div>
            )}
          </div>
          {(picks.length > 0 || streetPicks.length > 0) && (
            <div className="flex justify-end gap-3 pt-2 mt-2 border-t border-white/5 sticky bottom-0 bg-ink-900">
              {streetPicks.length > 0 && (
                <button
                  type="button"
                  onClick={() => onStreetsChange(undefined)}
                  className="text-[11px] text-slate-500 hover:text-slate-300"
                >
                  Clear streets
                </button>
              )}
              <button
                type="button"
                onClick={() => {
                  onChange(undefined);
                  onStreetsChange(undefined);
                  setOpen(false);
                }}
                className="text-[11px] text-slate-500 hover:text-slate-300"
              >
                Clear all
              </button>
            </div>
          )}
        </div>
      </Popover>
    </div>
  );
}

/** Multi-select dropdown backed by an (value, label, en) option list.
 *  Factored out because ``BuildingStatusDropdown`` and ``ConditionDropdown``
 *  share the exact same shape — one component, two call-sites. */
function MultiEnumDropdown({
  placeholder,
  options,
  value,
  onChange,
  width = "18rem",
}: {
  placeholder: string;
  options: { value: string; label: string; en: string }[];
  value: string[] | undefined;
  onChange: (v: string[] | undefined) => void;
  width?: string;
}) {
  const [open, setOpen] = useState(false);
  const picks = value || [];
  const picksSet = useMemo(() => new Set(picks), [picks]);

  let label: string = placeholder;
  if (picks.length === 1) {
    label = options.find((o) => o.value === picks[0])?.label ?? picks[0];
  } else if (picks.length > 1) {
    label = `${picks.length} ${placeholder.toLowerCase()}`;
  }

  const toggle = (v: string) => {
    const set = new Set(picks);
    if (set.has(v)) set.delete(v);
    else set.add(v);
    onChange(set.size ? Array.from(set) : undefined);
  };

  return (
    <div className="relative">
      <TriggerButton active={picks.length > 0} open={open} onClick={() => setOpen((o) => !o)}>
        {label}
      </TriggerButton>
      <Popover open={open} onClose={() => setOpen(false)}>
        <div className="flex flex-col gap-0.5" style={{ minWidth: width }}>
          {options.map((o) => (
            <MenuItem
              key={o.value}
              checked={picksSet.has(o.value)}
              onClick={() => toggle(o.value)}
            >
              <span className="flex flex-col gap-0.5 items-start">
                <span>{o.label}</span>
                <span className="text-[10px] text-slate-500 normal-case tracking-normal">
                  {o.en}
                </span>
              </span>
            </MenuItem>
          ))}
          {picks.length > 0 && (
            <div className="flex justify-end pt-2 mt-1 border-t border-white/5">
              <button
                type="button"
                onClick={() => { onChange(undefined); setOpen(false); }}
                className="text-[11px] text-slate-500 hover:text-slate-300"
              >
                Clear
              </button>
            </div>
          )}
        </div>
      </Popover>
    </div>
  );
}

function BuildingStatusDropdown({
  value,
  onChange,
}: {
  value: string[] | undefined;
  onChange: (v: string[] | undefined) => void;
}) {
  return (
    <MultiEnumDropdown
      placeholder="Status"
      options={BUILDING_STATUS_OPTIONS}
      value={value}
      onChange={onChange}
    />
  );
}

function ConditionDropdown({
  value,
  onChange,
}: {
  value: string[] | undefined;
  onChange: (v: string[] | undefined) => void;
}) {
  return (
    <MultiEnumDropdown
      placeholder="Condition"
      options={CONDITION_OPTIONS}
      value={value}
      onChange={onChange}
    />
  );
}

/** m² range dropdown. Modeled after PriceDropdown but with integer m² input
 *  and size-based quick picks (studio → large apartment → house size). */
function AreaDropdown({
  min,
  max,
  onChange,
}: {
  min?: number;
  max?: number;
  onChange: (min: number | undefined, max: number | undefined) => void;
}) {
  const [open, setOpen] = useState(false);
  const [localMin, setLocalMin] = useState<string>(min != null ? String(min) : "");
  const [localMax, setLocalMax] = useState<string>(max != null ? String(max) : "");

  useEffect(() => {
    setLocalMin(min != null ? String(min) : "");
    setLocalMax(max != null ? String(max) : "");
  }, [min, max]);

  const label = useMemo(() => {
    if (min != null && max != null) return `${min}–${max} m²`;
    if (max != null) return `≤ ${max} m²`;
    if (min != null) return `≥ ${min} m²`;
    return "Area";
  }, [min, max]);

  const commit = (closeAfter = true) => {
    const parsedMin = localMin === "" ? undefined : Number(localMin);
    const parsedMax = localMax === "" ? undefined : Number(localMax);
    onChange(
      Number.isFinite(parsedMin) && (parsedMin as number) >= 0 ? (parsedMin as number) : undefined,
      Number.isFinite(parsedMax) && (parsedMax as number) >= 0 ? (parsedMax as number) : undefined
    );
    if (closeAfter) setOpen(false);
  };

  return (
    <div className="relative">
      <TriggerButton
        active={min != null || max != null}
        open={open}
        onClick={() => setOpen((o) => !o)}
      >
        {label}
      </TriggerButton>
      <Popover
        open={open}
        onClose={() => {
          commit(false);
          setOpen(false);
        }}
      >
        <div className="min-w-[16rem] space-y-2.5">
          <div className="grid grid-cols-2 gap-2">
            <label className="block">
              <span className="block text-[10px] font-semibold uppercase tracking-wider text-slate-500 mb-1">
                Min m²
              </span>
              <input
                type="number"
                inputMode="numeric"
                min={0}
                placeholder="0"
                value={localMin}
                onChange={(e) => setLocalMin(e.target.value)}
                onKeyDown={(e) => { if (e.key === "Enter") commit(); }}
                className="block w-full rounded-md bg-ink-850 border border-white/10 px-2.5 py-1.5 text-[12px] text-slate-100 focus:border-accent-500/40 focus:outline-none"
              />
            </label>
            <label className="block">
              <span className="block text-[10px] font-semibold uppercase tracking-wider text-slate-500 mb-1">
                Max m²
              </span>
              <input
                type="number"
                inputMode="numeric"
                min={0}
                placeholder="any"
                value={localMax}
                onChange={(e) => setLocalMax(e.target.value)}
                onKeyDown={(e) => { if (e.key === "Enter") commit(); }}
                className="block w-full rounded-md bg-ink-850 border border-white/10 px-2.5 py-1.5 text-[12px] text-slate-100 focus:border-accent-500/40 focus:outline-none"
              />
            </label>
          </div>
          <div>
            <div className="text-[10px] font-semibold uppercase tracking-wider text-slate-500 mb-1">
              Quick picks
            </div>
            <div className="grid grid-cols-2 gap-1">
              {[
                { label: "Studio (< 40 m²)", min: undefined, max: 40 },
                { label: "40 – 70 m²", min: 40, max: 70 },
                { label: "70 – 120 m²", min: 70, max: 120 },
                { label: "120 m²+", min: 120, max: undefined },
              ].map((r) => (
                <button
                  key={r.label}
                  type="button"
                  onClick={() => {
                    setLocalMin(r.min != null ? String(r.min) : "");
                    setLocalMax(r.max != null ? String(r.max) : "");
                    onChange(r.min, r.max);
                    setOpen(false);
                  }}
                  className="text-left text-[11px] text-slate-300 hover:bg-white/[0.04] hover:text-slate-100 px-2 py-1.5 rounded-md border border-white/5 hover:border-white/15 transition-colors"
                >
                  {r.label}
                </button>
              ))}
            </div>
          </div>
          <div className="flex items-center justify-between pt-1 border-t border-white/5">
            <button
              type="button"
              onClick={() => {
                setLocalMin("");
                setLocalMax("");
                onChange(undefined, undefined);
                setOpen(false);
              }}
              className="text-[11px] text-slate-500 hover:text-slate-300"
            >
              Clear
            </button>
            <button
              type="button"
              onClick={() => commit()}
              className="text-[11px] font-semibold text-accent-300 hover:text-accent-200"
            >
              Apply
            </button>
          </div>
        </div>
      </Popover>
    </div>
  );
}

function SortDropdown({
  value,
  onChange,
}: {
  value: SortKey;
  onChange: (v: SortKey) => void;
}) {
  const [open, setOpen] = useState(false);
  const currentLabel = SORT_OPTIONS.find((s) => s.value === value)?.label ?? "Newest";
  return (
    <div className="relative">
      <TriggerButton active={value !== "newest"} open={open} onClick={() => setOpen((o) => !o)}>
        {currentLabel}
      </TriggerButton>
      <Popover open={open} onClose={() => setOpen(false)} align="right">
        <div className="flex flex-col gap-0.5 min-w-[15rem]">
          {SORT_OPTIONS.map((o, i) => {
            const prev = i > 0 ? SORT_OPTIONS[i - 1] : null;
            const showSeparator = prev && prev.group !== o.group;
            return (
              <div key={o.value}>
                {showSeparator && <div className="my-1 border-t border-white/5" />}
                <MenuItem
                  checked={value === o.value}
                  onClick={() => { onChange(o.value); setOpen(false); }}
                >
                  {o.label}
                </MenuItem>
              </div>
            );
          })}
        </div>
      </Popover>
    </div>
  );
}

/** Three-state filter against the viewer's agency placements: All / Uploaded
 *  by us / Not uploaded. Same trigger style as Source / Sort so the right
 *  cluster reads as a single row of equally-weighted controls. */
/** Date-range filter shaped for the ``MarketSearchIn`` ``listed_since`` /
 *  ``listed_until`` pair. ``mode`` drives the rendered chip + preset
 *  semantics; ``from`` / ``to`` are only consulted when ``mode === "custom"``
 *  (YYYY-MM-DD). Other modes recompute the cutoff at request time so
 *  "last 7 days" stays accurate as the page sits open. */
export type TimeRangeMode =
  | "all"
  | "1d"
  | "7d"
  | "30d"
  | "this_month"
  | "last_month"
  | "custom";
export interface TimeRangeValue {
  mode: TimeRangeMode;
  from?: string;
  to?: string;
}

/** Convert a ``TimeRangeValue`` into the ISO ``listed_since`` / ``listed_until``
 *  fragments the backend expects. Returns an empty object for "all time" so
 *  callers can spread it into the request body unconditionally. */
export function resolveTimeRange(v: TimeRangeValue): {
  listed_since?: string;
  listed_until?: string;
} {
  const day = 86_400_000;
  const now = Date.now();
  if (v.mode === "1d")
    return { listed_since: new Date(now - day).toISOString() };
  if (v.mode === "7d")
    return { listed_since: new Date(now - 7 * day).toISOString() };
  if (v.mode === "30d")
    return { listed_since: new Date(now - 30 * day).toISOString() };
  // Calendar-month windows (local time) — "this month" runs from the 1st
  // at 00:00 to now; "last month" is the full previous calendar month.
  if (v.mode === "this_month") {
    const n = new Date();
    const start = new Date(n.getFullYear(), n.getMonth(), 1, 0, 0, 0, 0);
    return { listed_since: start.toISOString() };
  }
  if (v.mode === "last_month") {
    const n = new Date();
    const start = new Date(n.getFullYear(), n.getMonth() - 1, 1, 0, 0, 0, 0);
    const thisMonthStart = new Date(n.getFullYear(), n.getMonth(), 1, 0, 0, 0, 0);
    return {
      listed_since: start.toISOString(),
      listed_until: new Date(thisMonthStart.getTime() - 1).toISOString(),
    };
  }
  if (v.mode === "custom") {
    const out: { listed_since?: string; listed_until?: string } = {};
    if (v.from) out.listed_since = new Date(v.from + "T00:00:00").toISOString();
    if (v.to) out.listed_until = new Date(v.to + "T23:59:59.999").toISOString();
    return out;
  }
  return {};
}

/** Trigger + popover with preset windows (24h / 7d / 30d) plus a custom
 *  from-to date picker. Built around the same primitives as the other
 *  filter dropdowns so it slots cleanly into any page-level toolbar. */
export function TimeRangeDropdown({
  value,
  onChange,
  showMonths = false,
}: {
  value: TimeRangeValue;
  onChange: (v: TimeRangeValue) => void;
  // Show calendar-month presets ("This month" / "Last month"). Off by
  // default so existing toolbars (Market, Competition) are unchanged.
  showMonths?: boolean;
}) {
  const [open, setOpen] = useState(false);
  const active = value.mode !== "all";
  const label = (() => {
    if (value.mode === "1d") return "Time · 24h";
    if (value.mode === "7d") return "Time · 7d";
    if (value.mode === "30d") return "Time · 30d";
    if (value.mode === "this_month") return "This month";
    if (value.mode === "last_month") return "Last month";
    if (value.mode === "custom") {
      if (value.from && value.to) return `${value.from} → ${value.to}`;
      if (value.from) return `from ${value.from}`;
      if (value.to) return `to ${value.to}`;
      return "Custom range";
    }
    return "Time";
  })();
  return (
    <div className="relative">
      <TriggerButton active={active} open={open} onClick={() => setOpen((o) => !o)}>
        {label}
      </TriggerButton>
      <Popover open={open} onClose={() => setOpen(false)} align="right">
        <div className="flex flex-col gap-0.5 min-w-[16rem]">
          <MenuItem
            checked={value.mode === "all"}
            onClick={() => { onChange({ mode: "all" }); setOpen(false); }}
          >
            All time
          </MenuItem>
          <MenuItem
            checked={value.mode === "1d"}
            onClick={() => { onChange({ mode: "1d" }); setOpen(false); }}
          >
            Last 24 hours
          </MenuItem>
          <MenuItem
            checked={value.mode === "7d"}
            onClick={() => { onChange({ mode: "7d" }); setOpen(false); }}
          >
            Last 7 days
          </MenuItem>
          <MenuItem
            checked={value.mode === "30d"}
            onClick={() => { onChange({ mode: "30d" }); setOpen(false); }}
          >
            Last 30 days
          </MenuItem>
          {showMonths && (
            <>
              <div className="my-1 border-t border-white/5" />
              <MenuItem
                checked={value.mode === "this_month"}
                onClick={() => { onChange({ mode: "this_month" }); setOpen(false); }}
              >
                This month
              </MenuItem>
              <MenuItem
                checked={value.mode === "last_month"}
                onClick={() => { onChange({ mode: "last_month" }); setOpen(false); }}
              >
                Last month
              </MenuItem>
            </>
          )}
          <div className="my-1 border-t border-white/5" />
          <div className="px-2.5 pt-1 pb-1">
            <div className="text-[11px] font-medium uppercase tracking-wider text-slate-500 mb-2">
              Custom range
            </div>
            <div className="flex flex-col gap-2">
              <label className="flex items-center gap-2 text-[11px] text-slate-400">
                <span className="w-9 text-right">From</span>
                <input
                  type="date"
                  className="flex-1 bg-ink-950/80 border border-white/10 rounded-md px-2 py-1 text-[12px] text-slate-200"
                  value={value.from ?? ""}
                  onChange={(e) =>
                    onChange({
                      mode: "custom",
                      from: e.target.value || undefined,
                      to: value.to,
                    })
                  }
                />
              </label>
              <label className="flex items-center gap-2 text-[11px] text-slate-400">
                <span className="w-9 text-right">To</span>
                <input
                  type="date"
                  className="flex-1 bg-ink-950/80 border border-white/10 rounded-md px-2 py-1 text-[12px] text-slate-200"
                  value={value.to ?? ""}
                  onChange={(e) =>
                    onChange({
                      mode: "custom",
                      from: value.from,
                      to: e.target.value || undefined,
                    })
                  }
                />
              </label>
            </div>
          </div>
        </div>
      </Popover>
    </div>
  );
}

export function PlacedByUsDropdown({
  value,
  onChange,
}: {
  value: Filters["placed_by_us"];
  onChange: (v: Filters["placed_by_us"]) => void;
}) {
  const [open, setOpen] = useState(false);
  const label =
    value === "placed"
      ? "Uploaded · ours"
      : value === "not_placed"
      ? "Uploaded · not ours"
      : "Uploaded";
  return (
    <div className="relative">
      <TriggerButton active={!!value} open={open} onClick={() => setOpen((o) => !o)}>
        {label}
      </TriggerButton>
      <Popover open={open} onClose={() => setOpen(false)} align="right">
        <div className="flex flex-col gap-0.5 min-w-[14rem]">
          <MenuItem
            checked={!value}
            onClick={() => { onChange(undefined); setOpen(false); }}
          >
            All listings
          </MenuItem>
          <MenuItem
            checked={value === "placed"}
            onClick={() => { onChange("placed"); setOpen(false); }}
          >
            Uploaded by us
          </MenuItem>
          <MenuItem
            checked={value === "not_placed"}
            onClick={() => { onChange("not_placed"); setOpen(false); }}
          >
            Not uploaded by us
          </MenuItem>
        </div>
      </Popover>
    </div>
  );
}

/** Pill-shaped toggle matching the dropdown visual weight. Lives next to
 *  sort so power-user options cluster on the right. */
function SourceDropdown({
  filters,
  onPatch,
}: {
  filters: Filters;
  onPatch: (p: Partial<Filters>) => void;
}) {
  const [open, setOpen] = useState(false);
  const activeCount =
    (filters.exclude_agency ? 1 : 0) +
    (filters.exclude_no_agents ? 1 : 0) +
    (filters.exclude_exclusive ? 1 : 0);
  const label = activeCount > 0 ? `Source · ${activeCount}` : "Source";

  return (
    <div className="relative">
      <TriggerButton
        active={activeCount > 0}
        open={open}
        onClick={() => setOpen((o) => !o)}
      >
        {label}
      </TriggerButton>
      <Popover open={open} onClose={() => setOpen(false)} align="right">
        <div className="w-[18rem] flex flex-col gap-1">
          <SourceCheckRow
            label="Owner-direct"
            description="Hide listings posted by an agency"
            checked={!!filters.exclude_agency}
            onChange={(v) => onPatch({ exclude_agency: v || undefined })}
          />
          <SourceCheckRow
            label="No-agents"
            description='Hide listings whose owner asked for no agencies'
            checked={!!filters.exclude_no_agents}
            onChange={(v) => onPatch({ exclude_no_agents: v || undefined })}
          />
          <SourceCheckRow
            label="Exclusive"
            description="Hide listings exclusive to another agency"
            checked={!!filters.exclude_exclusive}
            onChange={(v) => onPatch({ exclude_exclusive: v || undefined })}
          />
          {activeCount > 0 && (
            <div className="flex justify-end pt-2 mt-1 border-t border-white/5">
              <button
                type="button"
                onClick={() =>
                  onPatch({
                    exclude_agency: undefined,
                    exclude_no_agents: undefined,
                    exclude_exclusive: undefined,
                  })
                }
                className="text-[11px] text-slate-500 hover:text-slate-300"
              >
                Clear source
              </button>
            </div>
          )}
        </div>
      </Popover>
    </div>
  );
}

function SourceCheckRow({
  label,
  description,
  checked,
  onChange,
}: {
  label: string;
  description: string;
  checked: boolean;
  onChange: (v: boolean) => void;
}) {
  return (
    <button
      type="button"
      onClick={() => onChange(!checked)}
      className={`flex items-start gap-2.5 px-2 py-2 rounded-md text-left transition-colors ${
        checked
          ? "bg-accent-500/10 hover:bg-accent-500/15"
          : "hover:bg-white/[0.04]"
      }`}
    >
      <span
        className={`mt-[2px] inline-flex items-center justify-center w-4 h-4 rounded border transition-colors flex-shrink-0 ${
          checked
            ? "bg-accent-500 border-accent-400"
            : "border-slate-500"
        }`}
      >
        {checked && (
          <svg
            width="12"
            height="12"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="3.5"
            strokeLinecap="round"
            strokeLinejoin="round"
            className="text-ink-950"
          >
            <polyline points="20 6 9 17 4 12" />
          </svg>
        )}
      </span>
      <div className="flex-1 min-w-0">
        <div
          className={`text-[12px] font-medium ${
            checked ? "text-accent-100" : "text-slate-200"
          }`}
        >
          {label}
        </div>
        <div className="text-[11px] text-slate-500 leading-snug">{description}</div>
      </div>
    </button>
  );
}

// ───────────────────────────── Pagination ──────────────────────────────────
